import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';
import { AuthContext } from '../../context/AuthContext';
import { formatOrderNotes } from '../../utils/formatNotes';
import ConfiguracionSecuenciaModal from './ConfiguracionSecuenciaModal';

const Ordenes = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;

    const exchangeRate = parseFloat(localStorage.getItem('usdExchangeRate')) || 4000;
    const formatOrderValue = (val, method) => {
        const isCop = method && typeof method === 'string' && method.toLowerCase().includes('cop');
        const numericVal = parseFloat(val) || 0;
        if (isCop) {
            return `COP ${(numericVal * exchangeRate).toLocaleString('es-CO')}`;
        }
        return `$${numericVal.toFixed(2)}`;
    };

    const formatTableDisplay = (order) => {
        if (!order) return '';
        if (order.order_type === 'takeout' || (order.table_number && String(order.table_number).toLowerCase().includes('llevar'))) {
            return 'Para Llevar';
        }
        const raw = String(order.table_number || '').trim();
        if (!raw || raw.toUpperCase() === 'GENERICA' || raw.toUpperCase() === 'MESA GENERICA' || raw.toUpperCase() === 'MESA GENÉRICA' || raw.toUpperCase() === 'GENÉRICA') {
            return 'Mesa General';
        }
        if (raw.toLowerCase().startsWith('mesa')) {
            return raw.replace(/^mesa\s*/i, 'Mesa ');
        }
        return `Mesa ${raw}`;
    };

    const PAY_METHOD_LABELS = {
        efectivo: 'Efectivo USD',
        transferencia: 'Transferencia USD',
        efectivo_cop: 'Efectivo COP',
        transferencia_cop: 'Transferencia COP',
        tarjeta: 'Tarjeta'
    };
    const NOTE_LABEL_TO_METHOD = {
        'Efectivo USD': 'efectivo',
        'Transferencia USD': 'transferencia',
        'Efectivo COP': 'efectivo_cop',
        'Transferencia COP': 'transferencia_cop',
        'Tarjeta': 'tarjeta'
    };
    const isCopMethod = (method) => String(method || '').toLowerCase().includes('cop');

    // Muestra un monto USD en la moneda en que se cobró (COP solo si ese pago fue en pesos)
    const formatAmountInMethod = (usd, method) => {
        const val = parseFloat(usd) || 0;
        return isCopMethod(method)
            ? `COP ${Math.round(val * exchangeRate).toLocaleString('es-CO')}`
            : `$${val.toFixed(2)}`;
    };

    // Pagos registrados de la orden: [{ method, amount (USD), payer, created_at }]
    const getOrderPayments = (order) => {
        if (!order) return [];
        if (Array.isArray(order.payment_records) && order.payment_records.length > 0) {
            return order.payment_records.map(r => ({
                method: r.payment_method,
                amount: parseFloat(r.amount) || 0,
                payer: r.payer_name || '',
                created_at: r.created_at
            }));
        }
        // Órdenes anteriores: leer las notas [PAGO: ... - $X USD (Método)]
        const regex = /\[PAGO(?: PARCIAL)?:.*?-\s*\$([\d.]+) USD \((Efectivo USD|Efectivo COP|Transferencia USD|Transferencia COP|Tarjeta)\)/g;
        return [...(order.notes || '').matchAll(regex)].map(([, amount, label]) => ({
            method: NOTE_LABEL_TO_METHOD[label],
            amount: parseFloat(amount) || 0,
            payer: '',
            created_at: null
        }));
    };

    const getPaidUsd = (order) => getOrderPayments(order).reduce((acc, p) => acc + p.amount, 0);

    // Por producto: con qué método se pagó cada unidad { itemKey: [{ qty, method }] }
    const getItemPaymentMethods = (order) => {
        const result = {};
        const regex = /\[PAGO(?: PARCIAL)?:.*?\((Efectivo USD|Efectivo COP|Transferencia USD|Transferencia COP|Tarjeta)\).*?<!--\s*PAID_ITEMS:\s*(\{.*?\})\s*-->/g;
        [...((order && order.notes) || '').matchAll(regex)].forEach(([, label, json]) => {
            try {
                Object.entries(JSON.parse(json)).forEach(([key, qty]) => {
                    (result[key] = result[key] || []).push({ qty: parseInt(qty, 10) || 0, method: NOTE_LABEL_TO_METHOD[label] });
                });
            } catch (e) {
                // nota con formato inválido: se ignora
            }
        });
        return result;
    };

    // Totales por método (USD) de una orden, para el sumador de órdenes seleccionadas
    const getOrderMethodTotals = (order) => {
        const totals = { cash: 0, transfer: 0, card: 0 };
        const payments = getOrderPayments(order);
        if (payments.length > 0) {
            payments.forEach(p => {
                if (String(p.method).startsWith('efectivo')) totals.cash += p.amount;
                else if (String(p.method).startsWith('transferencia')) totals.transfer += p.amount;
                else totals.card += p.amount;
            });
            return totals;
        }
        if (order.payment_status !== 'paid') return totals;
        const method = String(order.payment_method || '').toLowerCase();
        const total = parseFloat(order.total) || 0;
        if (method.includes('efectivo') || method.includes('cash')) totals.cash += total;
        else if (method.includes('transfer')) totals.transfer += total;
        else if (method.includes('tarjeta') || method.includes('card')) totals.card += total;
        return totals;
    };

    // ===== Tiempos de la orden =====
    const formatDuration = (ms) => {
        if (ms === null || ms === undefined || isNaN(ms)) return '--';
        const totalSec = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const sec = totalSec % 60;
        if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
        return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    };

    // prep: desde que se guardó la orden hasta que se pasó al cliente
    // wait: desde que se pasó al cliente hasta que terminó de pagar
    const getOrderTimes = (order, nowMs) => {
        const start = new Date(order.confirmed_at || order.created_at).getTime();
        const delivered = order.delivered_at ? new Date(order.delivered_at).getTime() : null;
        const paid = order.paid_at ? new Date(order.paid_at).getTime() : null;
        const isCancelled = ['cancelled', 'rejected'].includes(String(order.status).toLowerCase());
        const end = isCancelled ? new Date(order.updated_at || order.created_at).getTime() : nowMs;
        return {
            prep: (delivered || paid || end) - start,
            prepRunning: !delivered && !paid && !isCancelled,
            wait: delivered ? (paid || end) - delivered : null,
            waitRunning: !!delivered && !paid && !isCancelled,
            total: (paid || end) - start
        };
    };

    const getPaymentMethodBadge = (method, paymentStatus) => {
        let badge;
        const m = String(method || '').toLowerCase();
        
        if (m.includes('dividido') || m.includes('mixto')) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f7ecd3', color: '#8f6520', border: '1px solid #efdca8', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><i className="bi bi-layers"></i> Mixto</span>;
        } else if (m.includes('efectivo_cop')) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f7ecd3', color: '#a2552a', border: '1px solid #efd6b0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><i className="bi bi-cash-stack"></i> Efectivo (COP)</span>;
        } else if (m.includes('transferencia_cop')) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f0e6d6', color: '#3a2618', border: '1px solid #e0c99a', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><i className="bi bi-bank"></i> Transfer (COP)</span>;
        } else if (m.includes('efectivo') || m.includes('cash')) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#e6efe0', color: '#46693f', border: '1px solid #a9c9a0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><i className="bi bi-cash-stack"></i> Efectivo</span>;
        } else if (m.includes('transferencia') || m.includes('transfer')) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f0e6d6', color: '#3a2618', border: '1px solid #d6c2a0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><i className="bi bi-bank"></i> Transferencia</span>;
        } else if (m.includes('tarjeta') || m.includes('card')) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f7f1e7', color: '#4a3222', border: '1px solid #d6c2a0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><i className="bi bi-credit-card"></i> Tarjeta</span>;
        } else if (!method) {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f4efe6', color: '#8a7d70', border: '1px solid #e6dfd3' }}>Sin definir</span>;
        } else {
            badge = <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f4efe6', color: '#6b5e51', border: '1px solid #e6dfd3' }}>{method}</span>;
        }

        if (paymentStatus === 'partially_paid') {
            return (
                <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
                    {badge}
                    <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '800', backgroundColor: '#f7ecd3', color: '#8f6520', border: '1px solid #efdca8', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <i className="bi bi-pie-chart-fill"></i> Pago Parcial
                    </span>
                </div>
            );
        }

        return badge;
    };

    // orders: tablero de activas (por cobrar) | gridOrders: página actual de Pagadas / Anuladas / Todas
    const [orders, setBoardOrders] = useState([]);
    const [gridOrders, setGridOrders] = useState([]);
    const [gridCount, setGridCount] = useState(0);
    const [counts, setCounts] = useState({ active: 0, completed: 0, cancelled: 0, all: 0, avg_prep_ms: null, avg_wait_ms: null });
    // Actualiza una orden esté donde esté
    const setOrders = (updater) => {
        setBoardOrders(updater);
        setGridOrders(updater);
    };
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); // 'active' (tablero) | 'completed' | 'cancelled' | 'all'
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [newPaymentMethod, setNewPaymentMethod] = useState('');
    const [editPaymentRecordId, setEditPaymentRecordId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 30;

    // Estado para modal de cobro directo con selección de productos
    const [showPayModal, setShowPayModal] = useState(false);
    const [payModalOrder, setPayModalOrder] = useState(null);
    const [paySelectedQuantities, setPaySelectedQuantities] = useState({});
    const [payMethod, setPayMethod] = useState('');
    const [payReference, setPayReference] = useState('');
    const [processingPay, setProcessingPay] = useState(false);
    const [singleCashGiven, setSingleCashGiven] = useState('');
    const [payerName, setPayerName] = useState('');
    // Modo de cobro: 'all' (todo lo pendiente) | 'items' (por productos) | 'split' (partes iguales) | 'custom' (monto libre)
    const [payMode, setPayMode] = useState('all');
    const [splitPeople, setSplitPeople] = useState(2);
    const [splitPartsNow, setSplitPartsNow] = useState(1);
    // Monto libre: una fila por persona con su monto (en la moneda de su método) y método de pago
    const makeCustomRow = (method = '') => ({ amount: '', method, reference: '' });
    const [customRows, setCustomRows] = useState([makeCustomRow(), makeCustomRow()]);
    // Cliente y descuento (se definen al cobrar)
    const [checkoutEdit, setCheckoutEdit] = useState(''); // '' | 'customer' | 'discount'
    const [custQuery, setCustQuery] = useState('');
    const [custResults, setCustResults] = useState([]);
    const [newCust, setNewCust] = useState(null); // formulario de cliente nuevo
    const [discountInput, setDiscountInput] = useState('');
    const [employeeFlag, setEmployeeFlag] = useState(false);
    const [savingCheckout, setSavingCheckout] = useState(false);

    // Reloj para los tiempos en vivo de las órdenes
    const [nowMs, setNowMs] = useState(Date.now());

    // Estado para configuración de secuencia / turno de orden
    const [showSequenceModal, setShowSequenceModal] = useState(false);

    // Estado para la calculadora de órdenes seleccionadas
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);

    const toggleSelectOrder = (orderId) => {
        setSelectedOrderIds(prev =>
            prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
        );
    };

    const clearSelection = () => {
        setSelectedOrderIds([]);
    };

    // Valores actuales para las funciones que corren desde el temporizador
    const gridQueryRef = useRef({});
    gridQueryRef.current = { statusFilter, page: currentPage, search: debouncedSearch };
    const fetchInFlightRef = useRef(false);
    const gridRequestRef = useRef(0);
    // Órdenes ya vistas: la calculadora conserva las seleccionadas al cambiar de página
    const knownOrdersRef = useRef(new Map());

    // Solo la página pedida (30 órdenes) de Pagadas / Anuladas / Todas
    const fetchGrid = async () => {
        const { statusFilter: tab, page, search } = gridQueryRef.current;
        if (tab === 'active') return;
        const requestId = ++gridRequestRef.current;
        const params = { page, page_size: itemsPerPage };
        if (tab !== 'all') params.scope = tab;
        if (search) params.search = search;
        try {
            const response = await api.get('/api/orders/orders/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                params
            });
            if (requestId !== gridRequestRef.current) return; // llegó una búsqueda más nueva
            setGridOrders(response.data.results || []);
            setGridCount(response.data.count || 0);
        } catch (err) {
            if (err.response?.status === 404 && page > 1) {
                setCurrentPage(1); // la página ya no existe
                return;
            }
            console.error('Error fetching orders page:', err);
        }
    };

    const fetchOrders = async () => {
        if (fetchInFlightRef.current) return; // no acumular peticiones si el servidor está lento
        fetchInFlightRef.current = true;
        try {
            const baseURL = process.env.REACT_APP_FAST_FOOD_SERVICE;
            // Tablero: solo las órdenes por cobrar. Una orden sin pagar nunca desaparece por antigua.
            const [activeRes, countsRes] = await Promise.all([
                api.get('/api/orders/orders/', { baseURL, params: { scope: 'active', page_size: 200 } }),
                api.get('/api/orders/orders/counts/', { baseURL })
            ]);
            setBoardOrders(activeRes.data.results || []);
            setCounts(countsRes.data);
            setError('');
            await fetchGrid();
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Error al cargar las órdenes');
        } finally {
            fetchInFlightRef.current = false;
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        // Refrescar para ver las órdenes nuevas del POS (solo si la pestaña está visible)
        const refresh = setInterval(() => {
            if (!document.hidden) fetchOrders();
        }, 20000);
        const onVisible = () => {
            if (!document.hidden) fetchOrders();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(refresh);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    // El reloj de los tiempos se pausa mientras se cobra: el cobro no debe trabarse por redibujar la pantalla
    useEffect(() => {
        if (showPayModal) return undefined;
        setNowMs(Date.now());
        const clock = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(clock);
    }, [showPayModal]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Espera a que termine de escribir antes de buscar en el servidor
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim().replace(/^#/, '')), 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchGrid();
    }, [statusFilter, currentPage, debouncedSearch]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showPayModal) setShowPayModal(false);
                else if (showModal) closeModal();
            }
        };

        if (showModal || showPayModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [showModal, showPayModal]);

    const handleChangePaymentMethod = async () => {
        if (!newPaymentMethod) return;
        
        try {
            const body = { payment_method: newPaymentMethod, exchange_rate: exchangeRate };
            if (editPaymentRecordId) body.record_id = editPaymentRecordId;
            const response = await api.post(
                `/api/orders/orders/${selectedOrder.order_number}/change_payment_method/`,
                body,
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE, timeout: 30000 }
            );

            // Actualizar la orden seleccionada
            setSelectedOrder(prev => ({ ...prev, ...response.data }));
            
            // Actualizar la orden en la lista principal
            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === selectedOrder.order_number
                        ? { ...order, ...response.data }
                        : order
                )
            );
            
            setIsEditingPayment(false);
        } catch (err) {
            console.error('Error changing payment method:', err);
            alert(`Error al cambiar método de pago: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleStatusChange = async (orderNumber, newStatus, event) => {
        if (event && event.stopPropagation) event.stopPropagation();
        setUpdatingStatus(prev => ({ ...prev, [orderNumber]: true }));
        try {
            const response = await api.post(
                `/api/orders/orders/${orderNumber}/update_status/`,
                { status: newStatus },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === orderNumber
                        ? { ...order, ...response.data }
                        : order
                )
            );

            if (selectedOrder && selectedOrder.order_number === orderNumber) {
                setSelectedOrder(prev => ({ ...prev, ...response.data }));
            }
        } catch (err) {
            console.error('Error updating status:', err);
            alert(`Error al actualizar el estado: ${err.response?.data?.detail || err.message}`);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [orderNumber]: false }));
        }
    };

    const handleQuickDeliver = async (order, event) => {
        if (event && event.stopPropagation) event.stopPropagation();
        await handleStatusChange(order.order_number, 'delivered', event);
    };

    // Funciones auxiliares para parsear pagos parciales previos y productos de la orden
    // Se pueden editar los productos mientras la orden no esté pagada, anulada ni facturada
    const canEditOrder = (order) => {
        const status = getNormalizedStatus(order.status);
        return status !== 'completed' && status !== 'cancelled' && order.payment_status !== 'paid' && !order.sri_id;
    };

    // Abre el Punto de Venta con los productos de la orden cargados para editarlos
    const handleEditOrder = (order, event) => {
        if (event && event.stopPropagation) event.stopPropagation();
        navigate('/fast-food/pos', {
            state: {
                editOrder: {
                    order_number: order.order_number,
                    order_sequence: order.order_sequence,
                    table_label: formatTableDisplay(order)
                }
            }
        });
    };

    const parsePaidQuantitiesFromNotes = (notes) => {
        if (!notes) return {};
        const paidMap = {};
        const regex = /<!--\s*PAID_ITEMS:\s*(\{.*?\})\s*-->/g;
        let match;
        while ((match = regex.exec(notes)) !== null) {
            try {
                const parsed = JSON.parse(match[1]);
                if (parsed && typeof parsed === 'object') {
                    Object.entries(parsed).forEach(([key, qty]) => {
                        const num = parseInt(qty, 10) || 0;
                        paidMap[key] = (paidMap[key] || 0) + num;
                    });
                }
            } catch (e) {
                console.warn('Error parsing paid items JSON from note:', e);
            }
        }
        return paidMap;
    };

    const getOrderItemsList = (order) => {
        if (!order || !order.items) return [];
        const paidMap = parsePaidQuantitiesFromNotes(order.notes);

        return order.items.map((item, idx) => {
            const unitPrice = parseFloat(item.unit_price) || 0;
            const lineTotal = parseFloat(item.line_total || item.subtotal) || (unitPrice * (item.quantity || 1));
            const quantity = item.quantity || 1;
            const unitTotal = (quantity > 0) ? (lineTotal / quantity) : unitPrice;
            const itemKey = item.id ? String(item.id) : `idx_${idx}`;
            const name = item.product_details?.name || item.product_name || `Producto #${idx + 1}`;
            
            const alreadyPaidQty = paidMap[itemKey] !== undefined ? paidMap[itemKey] : (paidMap[String(idx)] || 0);
            const remainingQty = Math.max(0, quantity - alreadyPaidQty);

            return {
                key: itemKey,
                id: item.id,
                name: name,
                quantity: quantity,
                alreadyPaidQty: alreadyPaidQty,
                remainingQty: remainingQty,
                isFullyPaid: remainingQty <= 0,
                unitPrice: unitPrice,
                lineTotal: lineTotal,
                unitTotal: unitTotal,
                notes: item.notes
            };
        });
    };

    // Cálculo del cobro actual según el modo (por productos o partes iguales)
    const getPayCalc = (order) => {
        const totalOrderUsd = parseFloat(order?.total || 0);
        const itemsList = getOrderItemsList(order);
        const paidUsd = getPaidUsd(order);
        const pendingUsd = Math.max(0, Math.round((totalOrderUsd - paidUsd) * 100) / 100);
        const itemsPaidUsd = itemsList.reduce((sum, it) => sum + (it.alreadyPaidQty * it.unitTotal), 0);
        // Si ya hubo pagos por monto (partes iguales o monto libre), los productos ya no reflejan lo pendiente
        const hasAmountPayments = paidUsd - itemsPaidUsd > 0.01;
        const mode = (hasAmountPayments && payMode === 'items') ? 'split' : payMode;

        if (mode === 'all') {
            return {
                mode, itemsList, totalOrderUsd, paidUsd, pendingUsd, hasAmountPayments,
                chargeUsd: pendingUsd, isFullPayment: true, paidItemsMap: null,
                summary: paidUsd > 0 ? 'Saldo pendiente' : 'Cuenta completa'
            };
        }

        if (mode === 'split') {
            const people = Math.max(1, parseInt(splitPeople, 10) || 1);
            const parts = Math.min(people, Math.max(1, parseInt(splitPartsNow, 10) || 1));
            const perPersonUsd = pendingUsd / people;
            const isFullPayment = parts >= people;
            const chargeUsd = isFullPayment ? pendingUsd : Math.round(perPersonUsd * parts * 100) / 100;
            return {
                mode, itemsList, totalOrderUsd, paidUsd, pendingUsd, hasAmountPayments,
                people, parts, perPersonUsd, chargeUsd, isFullPayment,
                paidItemsMap: null,
                summary: `Parte${parts > 1 ? 's' : ''} ${parts} de ${people} (partes iguales)`
            };
        }

        if (mode === 'custom') {
            // Cada persona escribe su monto en la moneda de su método de pago
            const rows = customRows.map((row, index) => {
                const typed = parseFloat(row.amount) || 0;
                const usd = isCopMethod(row.method) ? typed / exchangeRate : typed;
                return { ...row, index, usd: Math.round(usd * 100) / 100 };
            });
            const payingRows = rows.filter(r => r.usd > 0);
            const assignedUsd = Math.round(payingRows.reduce((sum, r) => sum + r.usd, 0) * 100) / 100;
            // Tolerancia por redondeo de conversiones COP
            const tolerance = 0.02 + 0.01 * payingRows.length;
            const exceedsPending = assignedUsd > pendingUsd + tolerance;
            const isFullPayment = assignedUsd > 0 && !exceedsPending && Math.abs(assignedUsd - pendingUsd) <= tolerance;
            return {
                mode, itemsList, totalOrderUsd, paidUsd, pendingUsd, hasAmountPayments,
                rows, payingRows, assignedUsd, exceedsPending,
                chargeUsd: exceedsPending ? 0 : (isFullPayment ? pendingUsd : assignedUsd),
                isFullPayment, paidItemsMap: null,
                perPersonUsd: pendingUsd / Math.max(1, rows.length),
                missingMethod: payingRows.some(r => !r.method),
                summary: 'Monto libre'
            };
        }

        const selectedItems = itemsList.filter(it => (paySelectedQuantities[it.key] || 0) > 0);
        const selectedUsd = Math.round(selectedItems.reduce((acc, it) => acc + ((paySelectedQuantities[it.key] || 0) * it.unitTotal), 0) * 100) / 100;
        const totalRemainingItems = itemsList.reduce((acc, it) => acc + it.remainingQty, 0);
        const totalSelectedNow = itemsList.reduce((acc, it) => acc + (paySelectedQuantities[it.key] || 0), 0);
        const isFullPayment = (totalRemainingItems > 0 && totalSelectedNow >= totalRemainingItems) || (selectedUsd > 0 && selectedUsd >= pendingUsd - 0.02);
        // Con descuento general los productos suman más que la cuenta: nunca se cobra más de lo pendiente
        const chargeUsd = isFullPayment ? pendingUsd : selectedUsd;
        const paidItemsMap = {};
        selectedItems.forEach(it => { paidItemsMap[it.key] = paySelectedQuantities[it.key]; });
        return {
            mode, itemsList, totalOrderUsd, paidUsd, pendingUsd, hasAmountPayments,
            chargeUsd, isFullPayment, paidItemsMap,
            summary: selectedItems.map(it => `${paySelectedQuantities[it.key]}x ${it.name}`).join(', ')
        };
    };

    // Funciones de control de selección de productos y cobro
    const handleOpenPayModal = async (order, event) => {
        if (event && event.stopPropagation) event.stopPropagation();
        
        // Siempre datos frescos: otro equipo pudo haberla cobrado hace un momento
        let fullOrder = order;
        try {
            const res = await api.get(`/api/orders/orders/${order.order_number}/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (res.data) fullOrder = res.data;
        } catch (e) {
            console.warn('No se pudo actualizar la orden antes de cobrar:', e);
            if (!order.items || order.items.length === 0) {
                alert('No se pudo cargar la orden. Intente de nuevo.');
                return;
            }
        }

        if (fullOrder.payment_status === 'paid' || ['cancelled', 'rejected'].includes(fullOrder.status)) {
            setOrders(prev => prev.map(o => o.order_number === fullOrder.order_number ? { ...o, ...fullOrder } : o));
            alert(fullOrder.payment_status === 'paid' ? 'Esta orden ya está pagada.' : 'Esta orden está anulada.');
            fetchOrders();
            return;
        }

        setPayModalOrder(fullOrder);
        // El POS crea todas las órdenes como 'efectivo': ese valor no significa que el cliente eligió efectivo.
        // La cajera debe elegir; solo se respeta un método distinto ya indicado a propósito.
        const currentMethod = PAY_METHOD_LABELS[fullOrder.payment_method] && fullOrder.payment_method !== 'efectivo'
            ? fullOrder.payment_method
            : '';
        setPayMethod(currentMethod);
        setPayReference('');
        setSingleCashGiven('');
        setPayerName('');
        setPayMode('all');
        setCheckoutEdit('');
        setCustQuery('');
        setCustResults([]);
        setNewCust(null);
        setSplitPeople(2);
        setSplitPartsNow(1);
        setCustomRows([makeCustomRow(currentMethod), makeCustomRow(currentMethod)]);

        // Por defecto seleccionamos todas las unidades PENDIENTES de cobro (remainingQty)
        const items = getOrderItemsList(fullOrder);
        const initialSelected = {};
        items.forEach(it => {
            initialSelected[it.key] = it.remainingQty;
        });
        setPaySelectedQuantities(initialSelected);

        setShowPayModal(true);
    };

    const handleItemQtyChange = (itemKey, delta, remainingQty) => {
        setPaySelectedQuantities(prev => {
            const cur = prev[itemKey] !== undefined ? prev[itemKey] : remainingQty;
            const next = Math.max(0, Math.min(remainingQty, cur + delta));
            return { ...prev, [itemKey]: next };
        });
    };

    const handleToggleItemSelection = (itemKey, remainingQty) => {
        setPaySelectedQuantities(prev => {
            const cur = prev[itemKey] || 0;
            return { ...prev, [itemKey]: cur > 0 ? 0 : remainingQty };
        });
    };

    const applyOrderUpdate = (data) => {
        setOrders(prev => prev.map(o => o.order_number === data.order_number ? { ...o, ...data } : o));
        setPayModalOrder(prev => (prev && prev.order_number === data.order_number ? { ...prev, ...data } : prev));
        if (selectedOrder && selectedOrder.order_number === data.order_number) {
            setSelectedOrder(prev => ({ ...prev, ...data }));
        }
    };

    const saveCustomerDiscount = async (payload) => {
        if (!payModalOrder) return false;
        setSavingCheckout(true);
        try {
            const res = await api.post(
                `/api/orders/orders/${payModalOrder.order_number}/set_customer_discount/`,
                payload,
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );
            applyOrderUpdate(res.data);
            setCheckoutEdit('');
            return true;
        } catch (err) {
            alert(err.response?.data?.detail || 'No se pudo actualizar la orden.');
            return false;
        } finally {
            setSavingCheckout(false);
        }
    };

    const searchCustomers = async (query) => {
        setCustQuery(query);
        if (query.trim().length < 3) {
            setCustResults([]);
            return;
        }
        try {
            const res = await api.post('/api/customers/admin/search/', { query }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setCustResults(res.data?.data?.customers || []);
        } catch (err) {
            console.error('Error buscando clientes:', err);
        }
    };

    const createAndAssignCustomer = async () => {
        if (!newCust.first_name.trim() || !newCust.email.trim()) {
            alert('Nombre y correo son obligatorios.');
            return;
        }
        setSavingCheckout(true);
        try {
            const res = await api.post('/api/customers/register/', {
                ...newCust,
                cedula: newCust.cedula || null,
                password: 'Password123!',
                password_confirmation: 'Password123!'
            }, { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE });
            const created = res.data?.data?.customer;
            setNewCust(null);
            if (created?.id) await saveCustomerDiscount({ customer_id: created.id });
        } catch (err) {
            const data = err.response?.data;
            const detail = data?.errors
                ? Object.entries(data.errors).map(([k, v]) => `- ${k}: ${v}`).join('\n')
                : (data?.message || err.message);
            alert(`Error al crear cliente:\n${detail}`);
        } finally {
            setSavingCheckout(false);
        }
    };

    // ===== Envío seguro del cobro =====
    const payAttemptRef = useRef(null);
    const payLockRef = useRef(false);

    const newPayKey = () => (window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`);

    // Mientras sea el mismo cobro se usa el mismo identificador: si la respuesta se perdió,
    // el servidor reconoce el reintento y no registra el dinero dos veces.
    const submitPayment = async (orderNumber, payload) => {
        const fingerprint = JSON.stringify([orderNumber, payload]);
        if (!payAttemptRef.current || payAttemptRef.current.fingerprint !== fingerprint) {
            payAttemptRef.current = { fingerprint, key: newPayKey() };
        }
        const send = () => api.post(
            `/api/orders/orders/${orderNumber}/mark_as_paid/`,
            { ...payload, idempotency_key: payAttemptRef.current.key },
            { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE, timeout: 30000 }
        );
        let response;
        try {
            response = await send();
        } catch (err) {
            if (err.response) throw err;
            // Sin respuesta (lentitud o mala conexión): un reintento automático con el mismo identificador
            await new Promise(resolve => setTimeout(resolve, 1500));
            response = await send();
        }
        payAttemptRef.current = null;
        return response;
    };

    const closePayModal = () => {
        setShowPayModal(false);
        setPayModalOrder(null);
    };

    // Tras un error se consulta la orden: muestra lo que de verdad quedó registrado
    const handlePaymentError = async (err, orderNumber, prefix) => {
        console.error('Error registrando cobro:', err);
        const conflict = err.response?.status === 409 ? err.response.data : null;
        if (conflict?.order) {
            applyOrderUpdate(conflict.order);
            closePayModal();
            alert(conflict.detail);
            fetchOrders();
            return;
        }
        const detail = err.response
            ? (err.response.data?.detail || err.message)
            : 'El servidor no respondió. Revise el saldo de la orden antes de volver a cobrar.';
        alert(`${prefix} ${detail}`);
        try {
            const res = await api.get(`/api/orders/orders/${orderNumber}/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            applyOrderUpdate(res.data);
            if (res.data.payment_status === 'paid') {
                closePayModal();
                alert('La orden ya quedó pagada.');
            }
            fetchOrders();
        } catch (refreshErr) {
            console.warn('No se pudo actualizar la orden:', refreshErr);
        }
    };

    // Monto libre: registra un pago por cada persona con monto, en orden
    const handleConfirmCustomRows = async (calc) => {
        if (calc.missingMethod) {
            alert('Seleccione el método de pago de cada persona.');
            return;
        }
        if (calc.exceedsPending) {
            alert(`Los montos suman $${calc.assignedUsd.toFixed(2)} y superan lo pendiente ($${calc.pendingUsd.toFixed(2)}).`);
            return;
        }
        if (calc.payingRows.length === 0) {
            alert('Ingrese cuánto paga cada persona.');
            return;
        }

        setProcessingPay(true);
        const total = calc.rows.length;
        let lastData = null;
        let registered = 0;
        try {
            for (let i = 0; i < calc.payingRows.length; i++) {
                const row = calc.payingRows[i];
                const isLast = i === calc.payingRows.length - 1;
                const closesOrder = isLast && calc.isFullPayment;
                // El último pago absorbe las diferencias de redondeo cuando completa la cuenta
                const amount = closesOrder
                    ? Math.round((calc.pendingUsd - calc.payingRows.slice(0, -1).reduce((s, r) => s + r.usd, 0)) * 100) / 100
                    : row.usd;
                const isCash = row.method === 'efectivo' || row.method === 'efectivo_cop';
                const isTransfer = row.method.includes('transferencia');
                const methodLabel = PAY_METHOD_LABELS[row.method] || 'Tarjeta';
                const refLabel = row.reference.trim() ? ` [Ref: ${row.reference.trim()}]` : '';
                const payer = `Persona ${row.index + 1}`;

                const response = await submitPayment(payModalOrder.order_number, {
                    payment_method: row.method,
                    payment_reference: row.reference.trim(),
                    amount,
                    cash_amount: isCash ? amount : 0,
                    transfer_amount: isTransfer ? amount : 0,
                    exchange_rate: isCopMethod(row.method) ? exchangeRate : null,
                    amount_cop: isCopMethod(row.method) ? (parseFloat(row.amount) || null) : null,
                    payer_name: payer,
                    notes: `[${closesOrder ? 'PAGO' : 'PAGO PARCIAL'}: ${payer} de ${total} (monto libre) - $${amount.toFixed(2)} USD (${methodLabel})${refLabel}]`,
                    is_partial: !closesOrder
                });
                lastData = response.data;
                registered++;
            }
        } catch (err) {
            await handlePaymentError(
                err,
                payModalOrder.order_number,
                `Se registraron ${registered} de ${calc.payingRows.length} pagos.`
            );
        } finally {
            setProcessingPay(false);
        }

        if (!lastData) return;
        applyOrderUpdate(lastData);
        fetchOrders();
        if ((registered === calc.payingRows.length && calc.isFullPayment) || lastData.payment_status === 'paid') {
            setShowPayModal(false);
            setPayModalOrder(null);
        } else {
            // Quedan personas o saldo: dejar solo las filas que no pagaron
            const pendingRows = calc.rows.filter(r => !(r.usd > 0) || calc.payingRows.indexOf(r) >= registered);
            setCustomRows(pendingRows.length > 0
                ? pendingRows.map(r => ({ amount: r.amount, method: r.method, reference: r.reference }))
                : [makeCustomRow(payMethod)]);
        }
    };

    const handleConfirmPayment = async () => {
        if (!payModalOrder || payLockRef.current) return; // evita el doble clic
        payLockRef.current = true;
        try {
            await confirmPayment();
        } finally {
            payLockRef.current = false;
        }
    };

    const confirmPayment = async () => {
        const calc = getPayCalc(payModalOrder);
        const chargeUsd = calc.chargeUsd;

        if (calc.mode === 'custom') {
            await handleConfirmCustomRows(calc);
            return;
        }
        if (!payMethod) {
            alert('Seleccione cómo paga el cliente.');
            return;
        }
        if (chargeUsd <= 0.001) {
            alert(calc.mode === 'split' ? 'No hay saldo pendiente para dividir.' : 'Debe seleccionar al menos 1 producto para realizar el cobro.');
            return;
        }

        const isCash = payMethod === 'efectivo' || payMethod === 'efectivo_cop';
        const isCop = payMethod.includes('cop');
        const isTransfer = payMethod.includes('transferencia');

        if (isCash && singleCashGiven !== '') {
            const given = parseFloat(singleCashGiven) || 0;
            const targetAmount = isCop ? (chargeUsd * exchangeRate) : chargeUsd;
            if (given < targetAmount - 0.05) {
                alert(`El efectivo recibido (${isCop ? `COP ${given.toLocaleString('es-CO')}` : `$${given.toFixed(2)} USD`}) es menor al monto a cobrar (${isCop ? `COP ${Math.round(targetAmount).toLocaleString('es-CO')}` : `$${targetAmount.toFixed(2)} USD`}).`);
                return;
            }
        }

        setProcessingPay(true);
        try {
            const payerLabel = payerName.trim() ? `${payerName.trim()}: ` : '';
            const methodLabel = PAY_METHOD_LABELS[payMethod] || 'Tarjeta';
            const refLabel = payReference.trim() ? ` [Ref: ${payReference.trim()}]` : '';
            const paymentTag = calc.isFullPayment ? 'PAGO' : 'PAGO PARCIAL';
            const itemsTag = calc.paidItemsMap ? ` <!-- PAID_ITEMS:${JSON.stringify(calc.paidItemsMap)} -->` : '';
            const paymentNote = `[${paymentTag}: ${payerLabel}${calc.summary} - $${chargeUsd.toFixed(2)} USD (${methodLabel})${refLabel}]${itemsTag}`;

            const payload = {
                payment_method: payMethod,
                payment_reference: payReference || '',
                amount: chargeUsd,
                cash_amount: isCash ? chargeUsd : 0,
                transfer_amount: isTransfer ? chargeUsd : 0,
                exchange_rate: isCop ? exchangeRate : null,
                amount_cop: isCop ? Math.round(chargeUsd * exchangeRate) : null,
                payer_name: payerName.trim(),
                notes: paymentNote,
                is_partial: !calc.isFullPayment
            };

            const response = await submitPayment(payModalOrder.order_number, payload);

            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === payModalOrder.order_number
                        ? { ...order, ...response.data }
                        : order
                )
            );

            if (selectedOrder && selectedOrder.order_number === payModalOrder.order_number) {
                setSelectedOrder(prev => ({ ...prev, ...response.data }));
            }

            fetchOrders();

            if (calc.mode === 'split' && !calc.isFullPayment && response.data.payment_status !== 'paid') {
                // Partes iguales: dejar el modal abierto para cobrar a la siguiente persona
                setPayModalOrder(prev => ({ ...prev, ...response.data }));
                setSplitPeople(calc.people - calc.parts);
                setSplitPartsNow(1);
                setSingleCashGiven('');
                setPayerName('');
                setPayReference('');
            } else {
                setShowPayModal(false);
                setPayModalOrder(null);
            }
        } catch (err) {
            await handlePaymentError(err, payModalOrder.order_number, 'Error al registrar cobro:');
        } finally {
            setProcessingPay(false);
        }
    };

    const handleRowClick = async (order) => {
        setShowModal(true);
        setSelectedOrder({ ...order, loading: true });

        try {
            const response = await api.get(
                `/api/orders/orders/${order.order_number}/`,
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            let orderData = response.data;
            orderData.customer_name = orderData.customer_name || order.customer_name;

            // Si tiene comprobante emitido pero aún no está en AUTHORIZED, auto-consultar a KoaDoc
            if (orderData.sri_id && orderData.sri_status !== 'AUTHORIZED') {
                try {
                    const statusRes = await billingService.getOrderInvoiceStatus(orderData.id);
                    if (statusRes.success && statusRes.data) {
                        orderData = {
                            ...orderData,
                            sri_status: statusRes.data.status || orderData.sri_status,
                            sri_pdf_url: statusRes.data.pdf_file_url || orderData.sri_pdf_url,
                            sri_xml_url: statusRes.data.xml_file_url || orderData.sri_xml_url,
                            sri_document_number: statusRes.data.document_number || orderData.sri_document_number,
                            sri_access_key: statusRes.data.access_key || orderData.sri_access_key
                        };
                        setOrders(prev => prev.map(o => o.id === orderData.id ? { ...o, ...orderData } : o));
                    }
                } catch (e) {
                    console.warn('No se pudo refrescar estado SRI en vivo:', e);
                }
            }

            setSelectedOrder(orderData);
        } catch (err) {
            console.error('Error fetching order details:', err);
            alert('Error al cargar los detalles de la orden');
            setShowModal(false);
            setSelectedOrder(null);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    const handleCancelOrder = async () => {
        if (!selectedOrder) return;

        const reason = window.prompt(`¿Está seguro de anular la Orden ${selectedOrder.order_number}?\nPor favor, ingrese el motivo de la anulación:`);
        
        if (reason === null) return;
        if (reason.trim() === '') {
            alert('Debe ingresar un motivo para anular la orden.');
            return;
        }

        try {
            const response = await api.post(`/api/orders/orders/${selectedOrder.order_number}/cancel/`, {
                reason: reason
            }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            setOrders(prev => prev.map(o => 
                o.id === selectedOrder.id ? { ...o, ...response.data } : o
            ));
            alert('Orden anulada exitosamente');
            closeModal();
            fetchOrders();
        } catch (err) {
            console.error('Error cancelling order:', err);
            alert(`Error al anular la orden: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handlePrintTicket = async () => {
        if (!selectedOrder) return;

        try {
            const receiptData = {
                order_number: selectedOrder.order_number,
                order_sequence: selectedOrder.order_sequence,
                customer_name: selectedOrder.customer_name || 'CONSUMIDOR FINAL',
                table_number: selectedOrder.table_number || (selectedOrder.order_type === 'takeout' ? 'PARA LLEVAR' : 'MESA GENÉRICA'),
                items: selectedOrder.items.map(item => ({
                    name: item.product_details?.name || item.product_name || 'Producto',
                    quantity: item.quantity,
                    price: parseFloat(item.unit_price),
                    total: parseFloat(item.line_total || item.subtotal),
                    note: item.notes || ''
                })),
                subtotal: parseFloat(selectedOrder.subtotal),
                discount: parseFloat(selectedOrder.discount_amount || 0),
                tax: parseFloat(selectedOrder.tax_amount || 0),
                total: parseFloat(selectedOrder.total),
                payment_method: selectedOrder.payment_method,
                payment_reference: selectedOrder.payment_reference,
                sri_access_key: selectedOrder.sri_access_key,
                sri_document_number: selectedOrder.sri_document_number
            };

            await printerService.printReceipt(receiptData);
            alert('Ticket enviado a la impresora');
        } catch (error) {
            console.error('Error printing ticket:', error);
            alert('Error al imprimir el ticket. Verifique la conexión con el agente de impresión.');
        }
    };

    const handleOpenSequenceModal = () => {
        setShowSequenceModal(true);
    };

    const getStatusDisplay = (status) => {
        if (!status) return '';
        const s = status.toLowerCase();
        if (s === 'preparing' || s === 'en preparación' || s === 'en preparacion' || s === 'pending' || s === 'pendiente') {
            return 'En Preparación';
        }
        if (s === 'delivered' || s === 'entregado' || s === 'pasado al cliente' || s === 'ready' || s === 'listo') {
            return 'Pasado al Cliente';
        }
        if (s === 'completed' || s === 'completado' || s === 'pagado' || s === 'paid') {
            return 'Pagado';
        }
        if (s === 'cancelled' || s === 'cancelado' || s === 'anulada' || s === 'anulado' || s === 'rejected') {
            return 'Anulada';
        }
        return status;
    };

    const getNormalizedStatus = (status) => {
        if (!status) return 'preparing';
        const s = status.toLowerCase();
        if (['preparing', 'pending', 'confirmed'].includes(s)) return 'preparing';
        if (['delivered', 'ready', 'delivering'].includes(s)) return 'delivered';
        if (['completed'].includes(s)) return 'completed';
        if (['cancelled', 'rejected'].includes(s)) return 'cancelled';
        return s;
    };

    // Conteos para pestañas
    const countCompleted = counts.completed;
    const countCancelled = counts.cancelled;
    const countAll = counts.all;
    const countActive = counts.active;

    const matchesSearch = (order) => {
        const search = (searchTerm || '').toLowerCase().trim();
        if (!search) return true;
        const matchesOrderNumber = order.order_number?.toLowerCase().includes(search);
        const matchesCustomer = order.customer_name?.toLowerCase().includes(search);
        const matchesSequence = order.order_sequence && String(order.order_sequence).includes(search.replace('#', ''));
        const matchesTable = order.table_number && String(order.table_number).toLowerCase().includes(search);
        const matchesTableFormatted = formatTableDisplay(order).toLowerCase().includes(search);
        return matchesOrderNumber || matchesCustomer || matchesSequence || matchesTable || matchesTableFormatted;
    };

    const byOldest = (a, b) => new Date(a.created_at) - new Date(b.created_at);
    const byNewest = (a, b) => new Date(b.created_at) - new Date(a.created_at);

    // Tablero: órdenes activas (las más antiguas primero, son las que llevan más tiempo esperando)
    const boardPreparing = orders.filter(o => getNormalizedStatus(o.status) === 'preparing' && matchesSearch(o)).sort(byOldest);
    const boardDelivered = orders.filter(o => getNormalizedStatus(o.status) === 'delivered' && matchesSearch(o)).sort(byOldest);

    // Vista en cuadrícula (pagadas / anuladas / todas): el servidor entrega y filtra la página
    const totalPages = Math.ceil(gridCount / itemsPerPage);
    const paginatedOrders = gridOrders;

    orders.forEach(o => knownOrdersRef.current.set(o.id, o));
    gridOrders.forEach(o => knownOrdersRef.current.set(o.id, o));
    const selectedOrdersList = selectedOrderIds.map(id => knownOrdersRef.current.get(id)).filter(Boolean);
    const selectedCount = selectedOrdersList.length;
    const selectedTotalUSD = selectedOrdersList.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
    const selectedMethodTotals = selectedOrdersList.reduce((acc, o) => {
        const t = getOrderMethodTotals(o);
        return { cash: acc.cash + t.cash, transfer: acc.transfer + t.transfer, card: acc.card + t.card };
    }, { cash: 0, transfer: 0, card: 0 });
    const selectedCashUSD = selectedMethodTotals.cash;
    const selectedTransferUSD = selectedMethodTotals.transfer;
    const selectedCardUSD = selectedMethodTotals.card;

    // Promedios de tiempos del día (preparación y espera de pago), calculados en el servidor
    const avgPrepMs = counts.avg_prep_ms;
    const avgWaitMs = counts.avg_wait_ms;

    // Tiempo "alto" para resaltar la tarjeta (en minutos)
    const PREP_ALERT_MIN = 20;
    const WAIT_ALERT_MIN = 30;

    const renderTimeChip = (label, ms, running, alertMin, icon) => {
        const isAlert = running && ms / 60000 >= alertMin;
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: '700',
                backgroundColor: isAlert ? '#f7e5e0' : running ? '#f7f1e7' : '#f4efe6',
                color: isAlert ? '#96372c' : running ? '#3a2618' : '#6b5e51',
                border: `1px solid ${isAlert ? '#e6ad9f' : running ? '#e8d9c0' : '#e6dfd3'}`
            }}>
                <i className={`bi ${icon}`}></i>
                <span>{label}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: '800' }}>{formatDuration(ms)}</span>
                {running && <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isAlert ? '#b04a3d' : '#4a3222', animation: 'pulseDot 1.2s infinite' }}></span>}
            </div>
        );
    };

    const renderOrderCard = (order) => {
        const normStatus = getNormalizedStatus(order.status);
        const isPreparing = normStatus === 'preparing';
        const isDelivered = normStatus === 'delivered';
        const isCompleted = normStatus === 'completed';
        const isCancelled = normStatus === 'cancelled';
        const isSelected = selectedOrderIds.includes(order.id);
        const times = getOrderTimes(order, nowMs);
        const totalUsd = parseFloat(order.total) || 0;
        const paidUsd = getPaidUsd(order);
        const paidPct = totalUsd > 0 ? Math.min(100, (paidUsd / totalUsd) * 100) : 0;
        const items = order.items || [];
        const accent = isPreparing ? '#c9973a' : isDelivered ? '#4a3222' : isCompleted ? '#5f8a5e' : '#b04a3d';

        return (
            <div
                key={order.id}
                onClick={() => handleRowClick(order)}
                style={{
                    backgroundColor: isSelected ? '#faf7f1' : '#ffffff',
                    border: isSelected ? '2px solid #c9973a' : '1px solid #e6dfd3',
                    borderTop: `4px solid ${accent}`,
                    borderRadius: '12px',
                    padding: '12px 14px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    opacity: isCancelled ? 0.7 : 1,
                    transition: 'box-shadow 0.15s, transform 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 14px rgba(0,0,0,0.10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
            >
                {/* Encabezado: turno, mesa, selección */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        {order.order_sequence && (
                            <span style={{ padding: '3px 9px', borderRadius: '8px', fontSize: '15px', fontWeight: '900', backgroundColor: '#4a3222', color: '#ffffff' }}>
                                #{order.order_sequence}
                            </span>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: '800', color: '#2b211b', fontSize: '14px' }} title={order.order_number}>
                                {formatTableDisplay(order)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#8a7d70', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {order.customer_name || 'Cliente Casual'} • {new Date(order.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    </div>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelectOrder(order.id)}
                        title="Seleccionar para sumar"
                        style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#4a3222', flexShrink: 0 }}
                    />
                </div>

                {/* Productos */}
                {items.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#4a3f36', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {items.slice(0, 4).map((item, idx) => (
                            <div key={item.id || idx} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <strong>{item.quantity}x</strong> {item.product_details?.name || item.product_name || 'Producto'}
                            </div>
                        ))}
                        {items.length > 4 && (
                            <div style={{ color: '#8a7d70', fontStyle: 'italic' }}>+ {items.length - 4} producto(s) más</div>
                        )}
                    </div>
                )}

                {/* Tiempos */}
                {!isCancelled && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {renderTimeChip(times.prepRunning ? 'Preparando' : 'Preparación', times.prep, times.prepRunning, PREP_ALERT_MIN, 'bi-fire')}
                        {times.wait !== null && renderTimeChip(times.waitRunning ? 'Esperando pago' : 'Hasta pagar', times.wait, times.waitRunning, WAIT_ALERT_MIN, 'bi-hourglass-split')}
                    </div>
                )}

                {/* Total y pago */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '900', color: 'var(--primary-color, #4a3222)', fontSize: '17px' }}>
                        {formatOrderValue(order.total, order.payment_method)}
                    </span>
                    {(isCompleted || order.payment_status === 'partially_paid') && getPaymentMethodBadge(order.payment_method, order.payment_status)}
                </div>

                {order.payment_status === 'partially_paid' && (
                    <div>
                        <div style={{ height: '6px', borderRadius: '4px', backgroundColor: '#efdca8', overflow: 'hidden' }}>
                            <div style={{ width: `${paidPct}%`, height: '100%', backgroundColor: '#5f8a5e' }}></div>
                        </div>
                        <div style={{ fontSize: '11px', color: '#6f4d17', fontWeight: '700', marginTop: '3px' }}>
                            Pagado ${paidUsd.toFixed(2)} • Falta ${Math.max(0, totalUsd - paidUsd).toFixed(2)}
                        </div>
                    </div>
                )}

                {/* Acciones */}
                {!isCompleted && !isCancelled && (
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${1 + (isPreparing ? 1 : 0) + (canEditOrder(order) ? 1 : 0)}, 1fr)`, gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                        {isPreparing && (
                            <button
                                onClick={(e) => handleQuickDeliver(order, e)}
                                disabled={updatingStatus[order.order_number]}
                                style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#4a3222', color: '#ffffff', border: 'none', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                title="Avanzar orden a Pasado al Cliente"
                            >
                                <i className="bi bi-box-arrow-right"></i> Pasar al cliente
                            </button>
                        )}
                        {canEditOrder(order) && (
                            <button
                                onClick={(e) => handleEditOrder(order, e)}
                                style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#ffffff', color: '#4a3222', border: '1px solid #4a3222', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                title="Editar los productos de esta orden"
                            >
                                <i className="bi bi-pencil-square"></i> Editar
                            </button>
                        )}
                        <button
                            onClick={(e) => handleOpenPayModal(order, e)}
                            style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#3d5c37', color: '#ffffff', border: 'none', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                            title="Cobrar orden"
                        >
                            <i className="bi bi-cash-coin"></i> Cobrar
                        </button>
                    </div>
                )}

                {isCompleted && (
                    <div style={{ fontSize: '11px', color: '#3d5c37', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <i className="bi bi-check-circle-fill"></i> Pagado
                        {order.sri_status === 'AUTHORIZED' && <span style={{ marginLeft: 'auto', color: '#46693f' }}><i className="bi bi-file-earmark-check"></i> SRI</span>}
                    </div>
                )}
            </div>
        );
    };

    const renderBoardColumn = (title, subtitle, list, color, bg, icon) => (
        <div style={{ backgroundColor: bg, borderRadius: '14px', padding: '12px', border: `1px solid ${color}33`, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', padding: '0 4px' }}>
                <div>
                    <div style={{ fontWeight: '800', fontSize: '15px', color, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className={`bi ${icon}`}></i> {title}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8a7d70' }}>{subtitle}</div>
                </div>
                <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: '800', backgroundColor: color, color: '#ffffff' }}>
                    {list.length}
                </span>
            </div>
            {list.length === 0 ? (
                <div style={{ padding: '28px 12px', textAlign: 'center', color: '#a89c8c', fontSize: '13px', fontWeight: '600' }}>
                    {searchTerm ? 'Sin coincidencias' : 'No hay órdenes aquí'}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '10px' }}>
                    {list.map(renderOrderCard)}
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#faf7f1' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        display: 'inline-block',
                        width: '48px',
                        height: '48px',
                        border: '4px solid #e6dfd3',
                        borderTopColor: 'var(--primary-color, #4a3222)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ marginTop: '16px', color: '#6b5e51', fontWeight: '500' }}>Cargando órdenes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#faf7f1' }}>
                <div style={{ backgroundColor: '#fcf3f0', borderLeft: '4px solid #c0574a', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: '448px' }}>
                    <p style={{ color: '#7a3a2a', fontWeight: '500' }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#faf7f1', padding: '24px' }}>
            <div style={{ maxWidth: '1380px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#2b211b', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <i className="bi bi-receipt" style={{ color: 'var(--primary-color, #4a3222)' }}></i> Gestión de Órdenes
                        </h1>
                        <p style={{ color: '#8a7d70', fontSize: '14px', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Flujo de pedidos en 3 estados: <strong>1. En Preparación</strong> <i className="bi bi-arrow-right text-muted"></i> <strong>2. Pasado al Cliente</strong> <i className="bi bi-arrow-right text-muted"></i> <strong>3. Pagado</strong>
                        </p>
                    </div>
                    <button
                        onClick={handleOpenSequenceModal}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 18px',
                            backgroundColor: '#4a3222',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(74, 50, 34, 0.2)',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2b211b'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a3222'}
                    >
                        <i className="bi bi-gear-fill"></i> Configurar Turno / N° Orden
                    </button>
                </div>

                {/* Pestañas de Estado (Filtro por Ciclo de Vida) */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    backgroundColor: '#ffffff',
                    padding: '8px',
                    borderRadius: '12px',
                    border: '1px solid #e6dfd3',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                    {[
                        { id: 'active', label: 'Activas (por cobrar)', count: countActive, color: '#4a3222', light: '#f4efe6', text: '#4a3222' },
                        { id: 'completed', label: 'Pagadas', count: countCompleted, color: '#5f8a5e', light: '#e6efe0', text: '#46693f' },
                        { id: 'cancelled', label: 'Anuladas', count: countCancelled, color: '#b04a3d', light: '#f7e5e0', text: '#96372c' },
                        { id: 'all', label: 'Todas', count: countAll, color: '#6b5e51', light: '#f4efe6', text: '#6b5e51' }
                    ].map(tab => {
                        const active = statusFilter === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setStatusFilter(tab.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    fontWeight: active ? '700' : '600',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    backgroundColor: active ? tab.color : tab.light,
                                    color: active ? '#ffffff' : tab.text,
                                    transition: 'all 0.2s'
                                }}
                            >
                                <span>{tab.label}</span>
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    backgroundColor: active ? 'rgba(255,255,255,0.25)' : '#ffffff',
                                    color: active ? '#ffffff' : tab.text
                                }}>
                                    {tab.count}
                                </span>
                            </button>
                        );
                    })}

                    {/* Promedios de tiempo del día */}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12px', color: '#6b5e51', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Promedio de hoy desde que se guarda la orden hasta que se pasa al cliente">
                            <i className="bi bi-fire" style={{ color: '#c9973a' }}></i> Prom. preparación: <strong>{formatDuration(avgPrepMs)}</strong>
                        </span>
                        <span style={{ fontSize: '12px', color: '#6b5e51', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Promedio de hoy desde que se pasa al cliente hasta que paga">
                            <i className="bi bi-hourglass-split" style={{ color: '#4a3222' }}></i> Prom. hasta pagar: <strong>{formatDuration(avgWaitMs)}</strong>
                        </span>
                    </div>
                </div>

                {/* Calculadora de Órdenes Seleccionadas */}
                {selectedCount > 0 && (
                    <div style={{
                        backgroundColor: '#faf7f1',
                        border: '2px solid #c9973a',
                        borderRadius: '12px',
                        padding: '16px 24px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '16px',
                        boxShadow: '0 4px 6px -1px rgba(74, 50, 34, 0.1)'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <i className="bi bi-calculator" style={{ fontSize: '20px', color: '#4a3222' }}></i>
                                <span style={{ fontWeight: '700', fontSize: '16px', color: '#4a3222' }}>
                                    Sumador de Órdenes Seleccionadas ({selectedCount} órdenes)
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '20px', fontSize: '14px', flexWrap: 'wrap', marginTop: '6px' }}>
                                <span style={{ color: '#46693f', fontWeight: '500' }}>
                                    <i className="bi bi-cash-stack" style={{ marginRight: '4px' }}></i> Efectivo: <strong>${selectedCashUSD.toFixed(2)}</strong>
                                </span>
                                <span style={{ color: '#3a2618', fontWeight: '500' }}>
                                    <i className="bi bi-bank" style={{ marginRight: '4px' }}></i> Transferencia: <strong>${selectedTransferUSD.toFixed(2)}</strong>
                                </span>
                                <span style={{ color: '#4a3222', fontWeight: '500' }}>
                                    <i className="bi bi-credit-card" style={{ marginRight: '4px' }}></i> Tarjeta: <strong>${selectedCardUSD.toFixed(2)}</strong>
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#4a3222', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Sumado</div>
                                <div style={{ fontSize: '28px', fontWeight: '800', color: '#4a3222' }}>
                                    ${selectedTotalUSD.toFixed(2)}
                                </div>
                            </div>
                            <button
                                onClick={clearSelection}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    backgroundColor: '#ffffff',
                                    border: '1.5px solid #d6ccbc',
                                    color: '#6b5e51',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f4efe6'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
                            >
                                Limpiar Selección
                            </button>
                        </div>
                    </div>
                )}

                {/* Search Bar */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e6dfd3', padding: '14px 16px', marginBottom: '20px' }}>
                    <input
                        type="text"
                        placeholder="Buscar por turno, mesa, cliente o número de orden..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #d6ccbc',
                            borderRadius: '8px',
                            outline: 'none',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#4a3222';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 50, 34, 0.15)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#d6ccbc';
                            e.target.style.boxShadow = 'none';
                        }}
                    />
                </div>

                {/* Tablero de órdenes activas / cuadrícula */}
                {statusFilter === 'active' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', alignItems: 'start' }}>
                        {renderBoardColumn('Sin pasar al cliente', 'En preparación', boardPreparing, '#b07f2b', '#fcf7ea', 'bi-fire')}
                        {renderBoardColumn('Pasadas al cliente', 'Pendientes de pago', boardDelivered, '#4a3222', '#faf6ee', 'bi-person-check-fill')}
                    </div>
                ) : (
                    <div>
                        {paginatedOrders.length === 0 ? (
                            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e6dfd3', padding: '48px 24px', textAlign: 'center', color: '#8a7d70', fontWeight: '500' }}>
                                {searchTerm ? 'No se encontraron órdenes con ese criterio' : 'No hay órdenes en esta categoría'}
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                                {paginatedOrders.map(renderOrderCard)}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '12px 16px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e6dfd3', flexWrap: 'wrap', gap: '8px' }}>
                                <p style={{ color: '#8a7d70', fontSize: '14px', margin: 0 }}>
                                    Mostrando del {(currentPage - 1) * itemsPerPage + 1} al {Math.min(currentPage * itemsPerPage, gridCount)} de {gridCount} órdenes
                                </p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        style={{ padding: '8px 16px', fontSize: '14px', borderRadius: '6px', border: '1px solid #d6ccbc', backgroundColor: currentPage === 1 ? '#f4efe6' : 'white', color: currentPage === 1 ? '#a89c8c' : '#4a3f36', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '500' }}
                                    >
                                        Anterior
                                    </button>
                                    <span style={{ padding: '8px 16px', fontSize: '14px', color: '#6b5e51', fontWeight: '500', backgroundColor: 'white', border: '1px solid #d6ccbc', borderRadius: '6px' }}>
                                        {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        style={{ padding: '8px 16px', fontSize: '14px', borderRadius: '6px', border: '1px solid #d6ccbc', backgroundColor: currentPage === totalPages ? '#f4efe6' : 'white', color: currentPage === totalPages ? '#a89c8c' : '#4a3f36', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '500' }}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal de Cobro (simplificado) */}
            {showPayModal && payModalOrder && (() => {
                const calc = getPayCalc(payModalOrder);
                const previousPayments = getOrderPayments(payModalOrder);
                const chargeUsd = calc.chargeUsd;
                const isCop = isCopMethod(payMethod);
                const isCash = payMethod === 'efectivo' || payMethod === 'efectivo_cop';
                const chargeInMethod = isCop ? Math.round(chargeUsd * exchangeRate) : chargeUsd;
                const cashGiven = parseFloat(singleCashGiven) || 0;
                const change = cashGiven - chargeInMethod;
                const money = (usd) => formatAmountInMethod(usd, payMethod);
                const hasMethod = calc.mode === 'custom' ? !calc.missingMethod : !!payMethod;
                const canConfirm = !processingPay && chargeUsd > 0.001 && !calc.exceedsPending && hasMethod;

                const sectionTitle = (n, text) => (
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#8a7d70', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                        {n}. {text}
                    </div>
                );
                const stepper = (value, onMinus, onPlus, minusDisabled, plusDisabled) => (
                    <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #d6ccbc', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                        <button type="button" disabled={minusDisabled} onClick={(e) => { e.stopPropagation(); onMinus(); }}
                            style={{ width: '32px', height: '32px', border: 'none', background: 'none', color: minusDisabled ? '#d6ccbc' : '#4a3222', fontSize: '16px', fontWeight: '900', cursor: minusDisabled ? 'default' : 'pointer' }}>−</button>
                        <span style={{ minWidth: '34px', textAlign: 'center', fontWeight: '800', fontSize: '14px', color: '#2b211b' }}>{value}</span>
                        <button type="button" disabled={plusDisabled} onClick={(e) => { e.stopPropagation(); onPlus(); }}
                            style={{ width: '32px', height: '32px', border: 'none', background: 'none', color: plusDisabled ? '#d6ccbc' : '#4a3222', fontSize: '16px', fontWeight: '900', cursor: plusDisabled ? 'default' : 'pointer' }}>+</button>
                    </div>
                );

                return (
                    <div
                        onClick={() => setShowPayModal(false)}
                        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{ backgroundColor: '#ffffff', borderRadius: '16px', maxWidth: '520px', width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)', overflow: 'hidden', animation: 'modalSlide 0.2s ease-out' }}
                        >
                            {/* Encabezado + saldo */}
                            <div style={{ padding: '16px 20px', backgroundColor: '#4a3222', color: '#ffffff', flexShrink: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '13px', fontWeight: '700', opacity: 0.9 }}>
                                        Cobrar {payModalOrder.order_sequence ? `#${payModalOrder.order_sequence} • ` : ''}{formatTableDisplay(payModalOrder)}
                                    </span>
                                    <button onClick={() => setShowPayModal(false)} aria-label="Cerrar"
                                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#ffffff', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}>
                                        <i className="bi bi-x-lg" style={{ fontSize: '13px' }}></i>
                                    </button>
                                </div>
                                <div style={{ marginTop: '6px' }}>
                                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Falta cobrar</div>
                                    <div style={{ fontSize: '28px', fontWeight: '900', lineHeight: 1.1 }}>
                                        ${calc.pendingUsd.toFixed(2)}
                                        <span style={{ fontSize: '14px', fontWeight: '600', opacity: 0.85, marginLeft: '8px' }}>
                                            COP {Math.round(calc.pendingUsd * exchangeRate).toLocaleString('es-CO')}
                                        </span>
                                    </div>
                                    {calc.paidUsd > 0 && (
                                        <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>
                                            Total ${calc.totalOrderUsd.toFixed(2)} • Ya pagado: {previousPayments.map(pay => `${formatAmountInMethod(pay.amount, pay.method)} ${PAY_METHOD_LABELS[pay.method] || ''}`).join(' + ')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                {/* Cliente y descuento */}
                                {(() => {
                                    const customerLabel = payModalOrder.customer_name
                                        || (payModalOrder.customer ? `${payModalOrder.customer.first_name || ''} ${payModalOrder.customer.last_name || ''}`.trim() : '')
                                        || 'Consumidor Final';
                                    const discountPct = parseFloat(payModalOrder.discount_percentage) || 0;
                                    const isEmployee = (payModalOrder.notes || '').includes('[VENTA_EMPLEADO]');
                                    const discountLocked = !!payModalOrder.sri_id;
                                    const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', borderRadius: '10px', backgroundColor: '#faf7f1', border: '1px solid #e6dfd3' };
                                    const linkBtn = { background: 'none', border: 'none', color: '#4a3222', fontWeight: '700', fontSize: '12px', cursor: 'pointer', padding: 0 };
                                    const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d6ccbc', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
                                    return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {/* Cliente */}
                                            <div style={rowStyle}>
                                                <span style={{ fontSize: '13px', color: '#4a3f36', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    <i className="bi bi-person" style={{ marginRight: '6px' }}></i>
                                                    <strong>{customerLabel}</strong>
                                                </span>
                                                <button type="button" style={linkBtn} onClick={() => { setCheckoutEdit(checkoutEdit === 'customer' ? '' : 'customer'); setNewCust(null); }}>
                                                    {checkoutEdit === 'customer' ? 'Cerrar' : 'Cambiar'}
                                                </button>
                                            </div>
                                            {checkoutEdit === 'customer' && (
                                                <div style={{ padding: '10px', borderRadius: '10px', border: '1px solid #e6dfd3', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {!newCust ? (
                                                        <>
                                                            <input autoFocus type="text" placeholder="Buscar por nombre, cédula o teléfono..." value={custQuery}
                                                                onChange={(e) => searchCustomers(e.target.value)} style={inputStyle} />
                                                            <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                {custResults.map(c => (
                                                                    <button key={c.id} type="button" disabled={savingCheckout}
                                                                        onClick={() => saveCustomerDiscount({ customer_id: c.id })}
                                                                        style={{ textAlign: 'left', padding: '7px 10px', borderRadius: '8px', border: '1px solid #e6dfd3', backgroundColor: '#ffffff', cursor: 'pointer', fontSize: '13px' }}>
                                                                        <strong>{c.first_name} {c.last_name}</strong>
                                                                        <span style={{ color: '#8a7d70', marginLeft: '6px', fontSize: '12px' }}>{c.cedula || c.email}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                                <button type="button" disabled={savingCheckout} onClick={() => saveCustomerDiscount({ customer_id: null })}
                                                                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #d6ccbc', backgroundColor: '#ffffff', fontSize: '12px', fontWeight: '700', color: '#6b5e51', cursor: 'pointer' }}>
                                                                    Consumidor Final
                                                                </button>
                                                                <button type="button" onClick={() => setNewCust({ cedula: '', first_name: '', last_name: '', email: '', phone: '' })}
                                                                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#4a3222', fontSize: '12px', fontWeight: '700', color: '#ffffff', cursor: 'pointer' }}>
                                                                    <i className="bi bi-person-plus"></i> Nuevo cliente
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <input type="text" placeholder="Cédula / RUC" value={newCust.cedula} onChange={(e) => setNewCust({ ...newCust, cedula: e.target.value })} style={inputStyle} />
                                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                                <input type="text" placeholder="Nombres *" value={newCust.first_name} onChange={(e) => setNewCust({ ...newCust, first_name: e.target.value })} style={inputStyle} />
                                                                <input type="text" placeholder="Apellidos" value={newCust.last_name} onChange={(e) => setNewCust({ ...newCust, last_name: e.target.value })} style={inputStyle} />
                                                            </div>
                                                            <input type="email" placeholder="Correo *" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} style={inputStyle} />
                                                            <input type="text" placeholder="Teléfono" value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} style={inputStyle} />
                                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                                <button type="button" onClick={() => setNewCust(null)}
                                                                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #d6ccbc', backgroundColor: '#ffffff', fontSize: '12px', fontWeight: '700', color: '#6b5e51', cursor: 'pointer' }}>
                                                                    Volver
                                                                </button>
                                                                <button type="button" disabled={savingCheckout} onClick={createAndAssignCustomer}
                                                                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#4a3222', fontSize: '12px', fontWeight: '700', color: '#ffffff', cursor: 'pointer' }}>
                                                                    {savingCheckout ? 'Guardando...' : 'Crear y asignar'}
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {/* Descuento */}
                                            <div style={rowStyle}>
                                                <span style={{ fontSize: '13px', color: '#4a3f36' }}>
                                                    <i className="bi bi-percent" style={{ marginRight: '6px' }}></i>
                                                    Descuento: <strong>{discountPct > 0 ? `${discountPct}%${isEmployee ? ' (Empleado)' : ''} • -$${(parseFloat(payModalOrder.discount_amount) || 0).toFixed(2)}` : 'Ninguno'}</strong>
                                                </span>
                                                {discountLocked ? (
                                                    <span style={{ fontSize: '11px', color: '#a89c8c' }}>Factura emitida</span>
                                                ) : (
                                                    <button type="button" style={linkBtn} onClick={() => {
                                                        setCheckoutEdit(checkoutEdit === 'discount' ? '' : 'discount');
                                                        setDiscountInput(discountPct > 0 ? String(discountPct) : '');
                                                        setEmployeeFlag(isEmployee);
                                                    }}>
                                                        {checkoutEdit === 'discount' ? 'Cerrar' : 'Cambiar'}
                                                    </button>
                                                )}
                                            </div>
                                            {checkoutEdit === 'discount' && (
                                                <div style={{ padding: '10px', borderRadius: '10px', border: '1px solid #e6dfd3', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                        {[0, 10, 25, 50, 100].map(pct => (
                                                            <button key={pct} type="button"
                                                                onClick={() => { setDiscountInput(pct ? String(pct) : ''); setEmployeeFlag(false); }}
                                                                style={{ flex: 1, minWidth: '48px', padding: '7px', borderRadius: '8px', border: (parseFloat(discountInput) || 0) === pct && !employeeFlag ? '2px solid #4a3222' : '1px solid #d6ccbc', backgroundColor: '#ffffff', fontWeight: '700', fontSize: '12px', color: '#4a3f36', cursor: 'pointer' }}>
                                                                {pct === 0 ? 'Sin' : `${pct}%`}
                                                            </button>
                                                        ))}
                                                        <button type="button"
                                                            onClick={() => { setEmployeeFlag(true); setDiscountInput('15'); }}
                                                            style={{ flex: 1, minWidth: '80px', padding: '7px', borderRadius: '8px', border: employeeFlag ? '2px solid #4a3222' : '1px solid #d6ccbc', backgroundColor: employeeFlag ? '#4a3222' : '#ffffff', color: employeeFlag ? '#ffffff' : '#4a3f36', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>
                                                            <i className="bi bi-person-badge"></i> Empleado 15%
                                                        </button>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <input type="number" min="0" max="100" placeholder="Otro %" value={discountInput}
                                                            onChange={(e) => { setDiscountInput(e.target.value); setEmployeeFlag(false); }} style={inputStyle} />
                                                        <button type="button" disabled={savingCheckout}
                                                            onClick={() => saveCustomerDiscount({ discount_percentage: Math.max(0, Math.min(100, parseFloat(discountInput) || 0)), is_employee: employeeFlag })}
                                                            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#4a3222', color: '#ffffff', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                            {savingCheckout ? 'Aplicando...' : 'Aplicar'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* 1. Qué se cobra */}
                                <div>
                                    {sectionTitle(1, '¿Qué se cobra?')}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', backgroundColor: '#f4efe6', padding: '4px', borderRadius: '10px' }}>
                                        {[
                                            { id: 'all', label: 'Todo' },
                                            { id: 'items', label: 'Por productos' },
                                            { id: 'split', label: 'Partes iguales' },
                                            { id: 'custom', label: 'Monto libre' }
                                        ].map(m => {
                                            const active = calc.mode === m.id;
                                            const disabled = m.id === 'items' && calc.hasAmountPayments;
                                            return (
                                                <button key={m.id} type="button" disabled={disabled} onClick={() => setPayMode(m.id)}
                                                    title={disabled ? 'La cuenta ya se está dividiendo por montos' : ''}
                                                    style={{ padding: '9px 6px', borderRadius: '8px', border: 'none', backgroundColor: active ? '#ffffff' : 'transparent', color: disabled ? '#d6ccbc' : active ? '#4a3222' : '#6b5e51', fontWeight: active ? '800' : '600', fontSize: '13px', cursor: disabled ? 'not-allowed' : 'pointer', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>
                                                    {m.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {calc.mode === 'items' && (
                                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '240px', overflowY: 'auto' }}>
                                            {calc.itemsList.map(item => {
                                                const qty = paySelectedQuantities[item.key] || 0;
                                                const locked = item.remainingQty <= 0;
                                                return (
                                                    <div key={item.key}
                                                        onClick={() => { if (!locked) handleToggleItemSelection(item.key, item.remainingQty); }}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '10px', border: qty > 0 ? '2px solid #4a3222' : '1px solid #e6dfd3', backgroundColor: locked ? '#faf7f1' : '#ffffff', opacity: locked ? 0.55 : 1, cursor: locked ? 'default' : 'pointer' }}>
                                                        <i className={`bi ${locked ? 'bi-check-circle-fill' : qty > 0 ? 'bi-check-square-fill' : 'bi-square'}`}
                                                            style={{ fontSize: '18px', color: locked ? '#5f8a5e' : qty > 0 ? '#4a3222' : '#a89c8c' }}></i>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: '700', fontSize: '13px', color: '#2b211b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#8a7d70' }}>
                                                                {locked ? 'Pagado' : `$${item.unitTotal.toFixed(2)} c/u${item.alreadyPaidQty > 0 ? ` • faltan ${item.remainingQty}` : ''}`}
                                                            </div>
                                                        </div>
                                                        {!locked && item.remainingQty > 1 && stepper(
                                                            qty,
                                                            () => handleItemQtyChange(item.key, -1, item.remainingQty),
                                                            () => handleItemQtyChange(item.key, 1, item.remainingQty),
                                                            qty <= 0, qty >= item.remainingQty
                                                        )}
                                                        <span style={{ minWidth: '58px', textAlign: 'right', fontWeight: '800', fontSize: '13px', color: qty > 0 ? '#4a3222' : '#a89c8c' }}>
                                                            ${(qty * item.unitTotal).toFixed(2)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {calc.mode === 'split' && (
                                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', borderRadius: '10px', backgroundColor: '#faf7f1', border: '1px solid #e6dfd3' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#4a3f36' }}>
                                                    {calc.paidUsd > 0 ? 'Personas que faltan' : 'Personas'}
                                                </span>
                                                {stepper(
                                                    calc.people,
                                                    () => { setSplitPeople(Math.max(1, calc.people - 1)); setSplitPartsNow(1); },
                                                    () => { setSplitPeople(calc.people + 1); setSplitPartsNow(1); },
                                                    calc.people <= 1, false
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#4a3f36' }}>Pagan ahora</span>
                                                {stepper(
                                                    calc.parts,
                                                    () => setSplitPartsNow(Math.max(1, calc.parts - 1)),
                                                    () => setSplitPartsNow(Math.min(calc.people, calc.parts + 1)),
                                                    calc.parts <= 1, calc.parts >= calc.people
                                                )}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#4a3222', fontWeight: '700', textAlign: 'center' }}>
                                                Cada persona: ${calc.perPersonUsd.toFixed(2)} • COP {Math.round(calc.perPersonUsd * exchangeRate).toLocaleString('es-CO')}
                                            </div>
                                        </div>
                                    )}

                                    {calc.mode === 'custom' && (() => {
                                        const toMethod = (usd, method) => isCopMethod(method) ? String(Math.round(usd * exchangeRate)) : usd.toFixed(2);
                                        const setRow = (idx, patch) => setCustomRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
                                        const resizeRows = (n) => setCustomRows(prev => n > prev.length
                                            ? [...prev, ...Array.from({ length: n - prev.length }, () => makeCustomRow(payMethod))]
                                            : prev.slice(0, n));
                                        const splitEqually = () => {
                                            // Partes iguales al centavo; la última persona absorbe el redondeo
                                            const n = calc.rows.length;
                                            const base = Math.floor((calc.pendingUsd / n) * 100) / 100;
                                            setCustomRows(prev => prev.map((r, i) => ({
                                                ...r,
                                                amount: toMethod(i === n - 1 ? Math.round((calc.pendingUsd - base * (n - 1)) * 100) / 100 : base, r.method)
                                            })));
                                        };
                                        const remainingUsd = Math.round((calc.pendingUsd - calc.assignedUsd) * 100) / 100;
                                        return (
                                            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderRadius: '10px', backgroundColor: '#faf7f1', border: '1px solid #e6dfd3' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#4a3f36' }}>
                                                        {calc.paidUsd > 0 ? 'Personas que faltan' : '¿Cuántas personas?'}
                                                    </span>
                                                    {stepper(
                                                        calc.rows.length,
                                                        () => resizeRows(Math.max(1, calc.rows.length - 1)),
                                                        () => resizeRows(Math.min(20, calc.rows.length + 1)),
                                                        calc.rows.length <= 1, calc.rows.length >= 20
                                                    )}
                                                </div>

                                                {calc.rows.map((row, idx) => {
                                                    const rowCop = isCopMethod(row.method);
                                                    const othersUsd = calc.assignedUsd - row.usd;
                                                    const restUsd = Math.max(0, Math.round((calc.pendingUsd - othersUsd) * 100) / 100);
                                                    return (
                                                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', borderRadius: '8px', backgroundColor: '#ffffff', border: row.usd > 0 ? '1px solid #4a3222' : '1px solid #e6dfd3' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#2b211b', minWidth: '72px' }}>Persona {idx + 1}</span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step={rowCop ? '1000' : '0.01'}
                                                                    placeholder={rowCop ? 'COP' : 'USD'}
                                                                    value={row.amount}
                                                                    onChange={(e) => setRow(idx, { amount: e.target.value })}
                                                                    style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: '8px', border: '1px solid #d6ccbc', fontSize: '15px', fontWeight: '700', outline: 'none' }}
                                                                />
                                                                <select
                                                                    value={row.method}
                                                                    onChange={(e) => {
                                                                        // Convertir lo escrito si cambia la moneda (USD <-> COP)
                                                                        const method = e.target.value;
                                                                        const patch = { method };
                                                                        if (row.amount !== '' && isCopMethod(method) !== rowCop) patch.amount = toMethod(row.usd, method);
                                                                        setRow(idx, patch);
                                                                    }}
                                                                    style={{ padding: '8px 4px', borderRadius: '8px', border: '1px solid #d6ccbc', fontSize: '12px', backgroundColor: '#ffffff', maxWidth: '130px' }}
                                                                >
                                                                    <option value="" disabled>Método...</option>
                                                                    {Object.entries(PAY_METHOD_LABELS).map(([id, label]) => (
                                                                        <option key={id} value={id}>{label}</option>
                                                                    ))}
                                                                </select>
                                                                <button type="button" title="Poner lo que falta"
                                                                    onClick={() => setRow(idx, { amount: toMethod(restUsd, row.method) })}
                                                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #d6ccbc', backgroundColor: '#faf7f1', color: '#4a3f36', fontWeight: '700', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                    Resto
                                                                </button>
                                                            </div>
                                                            {row.method.includes('transfer') && (
                                                                <input
                                                                    type="text"
                                                                    placeholder="N° de referencia (opcional)"
                                                                    value={row.reference}
                                                                    onChange={(e) => setRow(idx, { reference: e.target.value })}
                                                                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e6dfd3', fontSize: '12px', outline: 'none' }}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                <button type="button" onClick={splitEqually}
                                                    style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #d6ccbc', backgroundColor: '#ffffff', color: '#4a3f36', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>
                                                    Repartir igual ({`$${calc.perPersonUsd.toFixed(2)}`} c/u)
                                                </button>

                                                <div style={{ fontSize: '12px', fontWeight: '700', color: calc.exceedsPending ? '#96372c' : '#4a3222', textAlign: 'center' }}>
                                                    Asignado ${calc.assignedUsd.toFixed(2)} de ${calc.pendingUsd.toFixed(2)}
                                                    {calc.exceedsPending
                                                        ? ` • Sobran $${(-remainingUsd).toFixed(2)}`
                                                        : calc.isFullPayment ? ' • Cuenta completa' : ` • Faltan $${remainingUsd.toFixed(2)}`}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* 2. Cómo paga (en monto libre cada persona elige su método) */}
                                {calc.mode !== 'custom' && (
                                <div>
                                    {sectionTitle(2, '¿Cómo paga?')}
                                    {!payMethod && (
                                        <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '700', color: '#96372c' }}>
                                            Seleccione el método de pago para continuar
                                        </div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '6px' }}>
                                        {[
                                            { id: 'efectivo', label: 'Efectivo', sub: 'USD', icon: 'bi-cash-stack' },
                                            { id: 'efectivo_cop', label: 'Efectivo', sub: 'COP', icon: 'bi-cash-stack' },
                                            { id: 'transferencia', label: 'Transfer.', sub: 'USD', icon: 'bi-bank' },
                                            { id: 'transferencia_cop', label: 'Transfer.', sub: 'COP', icon: 'bi-bank' },
                                            { id: 'tarjeta', label: 'Tarjeta', sub: 'USD', icon: 'bi-credit-card' }
                                        ].map(m => {
                                            const active = payMethod === m.id;
                                            return (
                                                <button key={m.id} type="button" onClick={() => { setPayMethod(m.id); setSingleCashGiven(''); }}
                                                    style={{ padding: '8px 4px', borderRadius: '10px', border: active ? '2px solid #4a3222' : '1px solid #d6ccbc', backgroundColor: active ? '#4a3222' : '#ffffff', color: active ? '#ffffff' : '#4a3f36', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                                    <i className={`bi ${m.icon}`} style={{ fontSize: '16px' }}></i>
                                                    <span style={{ fontSize: '12px', fontWeight: '700' }}>{m.label}</span>
                                                    <span style={{ fontSize: '10px', fontWeight: '800', opacity: 0.75 }}>{m.sub}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {isCash && (
                                        <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <input
                                                type="number"
                                                placeholder={`Recibido (${isCop ? 'COP' : 'USD'}) - opcional`}
                                                value={singleCashGiven}
                                                onChange={(e) => setSingleCashGiven(e.target.value)}
                                                style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #d6ccbc', fontSize: '14px', outline: 'none', minWidth: 0 }}
                                            />
                                            <button type="button" onClick={() => setSingleCashGiven(String(isCop ? chargeInMethod : chargeUsd.toFixed(2)))}
                                                style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d6ccbc', backgroundColor: '#faf7f1', color: '#4a3f36', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                Exacto
                                            </button>
                                        </div>
                                    )}
                                    {isCash && singleCashGiven !== '' && (
                                        <div style={{ marginTop: '6px', fontSize: '14px', fontWeight: '800', color: change >= -0.01 ? '#46693f' : '#96372c' }}>
                                            {change >= -0.01
                                                ? `Vuelto: ${isCop ? `COP ${Math.round(change).toLocaleString('es-CO')}` : `$${change.toFixed(2)}`}`
                                                : `Faltan: ${isCop ? `COP ${Math.round(-change).toLocaleString('es-CO')}` : `$${(-change).toFixed(2)}`}`}
                                        </div>
                                    )}

                                    {payMethod.includes('transfer') && (
                                        <input
                                            type="text"
                                            placeholder="N° de referencia (opcional)"
                                            value={payReference}
                                            onChange={(e) => setPayReference(e.target.value)}
                                            style={{ marginTop: '10px', width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d6ccbc', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                                        />
                                    )}

                                    {calc.mode !== 'all' && (
                                        <input
                                            type="text"
                                            placeholder="¿Quién paga? (opcional)"
                                            value={payerName}
                                            onChange={(e) => setPayerName(e.target.value)}
                                            style={{ marginTop: '10px', width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e6dfd3', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }}
                                        />
                                    )}
                                </div>
                                )}
                            </div>

                            {/* Botón de cobro */}
                            <div style={{ padding: '14px 20px', borderTop: '1px solid #e6dfd3', display: 'flex', gap: '10px', flexShrink: 0 }}>
                                <button type="button" onClick={() => setShowPayModal(false)} disabled={processingPay}
                                    style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#ffffff', color: '#6b5e51', border: '1px solid #d6ccbc', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                                    Cancelar
                                </button>
                                <button type="button" onClick={handleConfirmPayment} disabled={!canConfirm}
                                    style={{ flex: 1, padding: '10px 16px', borderRadius: '10px', backgroundColor: !canConfirm ? '#a89c8c' : calc.isFullPayment ? '#3d5c37' : '#a2552a', color: '#ffffff', border: 'none', cursor: canConfirm ? 'pointer' : 'not-allowed', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                    <span style={{ fontSize: '16px', fontWeight: '900' }}>
                                        {processingPay ? 'Procesando...' : calc.mode === 'custom'
                                            ? `Cobrar $${chargeUsd.toFixed(2)} (${calc.payingRows.length} pago${calc.payingRows.length === 1 ? '' : 's'})`
                                            : `Cobrar ${money(chargeUsd)}`}
                                    </span>
                                    {!processingPay && (
                                        <span style={{ fontSize: '11px', fontWeight: '600', opacity: 0.9 }}>
                                            {calc.isFullPayment ? 'Cierra la orden como pagada' : `Pago parcial • quedará ${calc.mode === 'custom' ? `$${Math.max(0, calc.pendingUsd - chargeUsd).toFixed(2)}` : formatAmountInMethod(Math.max(0, calc.pendingUsd - chargeUsd), payMethod)}`}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Order Details Modal */}
            {showModal && selectedOrder && (
                <div
                    onClick={closeModal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            maxWidth: '640px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{ padding: '24px', borderBottom: '1px solid #e6dfd3' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {selectedOrder.order_sequence && (
                                            <span style={{
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: '800',
                                                backgroundColor: '#4a3222',
                                                color: '#ffffff'
                                            }}>
                                                ORDEN #{selectedOrder.order_sequence}
                                            </span>
                                        )}
                                        <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#2b211b', margin: 0 }}>
                                            {selectedOrder.order_number}
                                        </h2>
                                    </div>
                                    <p style={{ color: '#8a7d70', fontSize: '14px', marginTop: '4px', marginBottom: 0 }}>
                                        {new Date(selectedOrder.created_at).toLocaleString('es-ES', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button
                                        onClick={handlePrintTicket}
                                        style={{
                                            background: '#4a3222',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '8px 14px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                        aria-label="Imprimir orden"
                                    >
                                        <i className="bi bi-printer-fill"></i> Imprimir
                                    </button>
                                    <button
                                        onClick={closeModal}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '24px',
                                            cursor: 'pointer',
                                            color: '#8a7d70',
                                            padding: '4px 8px',
                                            lineHeight: 1
                                        }}
                                        aria-label="Cerrar modal"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            {/* Barra de Progreso del Ciclo de 3 Estados */}
                            <div style={{
                                padding: '14px',
                                backgroundColor: '#faf7f1',
                                borderRadius: '12px',
                                border: '1px solid #e6dfd3',
                                marginBottom: '14px'
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#8a7d70', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                    Ciclo de Vida de la Orden
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {/* Paso 1: En Preparación */}
                                    <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 10px',
                                        borderRadius: '8px',
                                        backgroundColor: (getNormalizedStatus(selectedOrder.status) === 'preparing') ? '#f7ecd3' : (['delivered', 'completed'].includes(getNormalizedStatus(selectedOrder.status))) ? '#e6efe0' : '#f4efe6',
                                        border: (getNormalizedStatus(selectedOrder.status) === 'preparing') ? '2px solid #c9973a' : (['delivered', 'completed'].includes(getNormalizedStatus(selectedOrder.status))) ? '1px solid #a9c9a0' : '1px solid #e6dfd3',
                                        color: (getNormalizedStatus(selectedOrder.status) === 'preparing') ? '#6f4d17' : (['delivered', 'completed'].includes(getNormalizedStatus(selectedOrder.status))) ? '#3d5c37' : '#8a7d70',
                                        fontWeight: (getNormalizedStatus(selectedOrder.status) === 'preparing') ? '800' : '600',
                                        fontSize: '11px'
                                    }}>
                                        <i className={`bi ${(['delivered', 'completed'].includes(getNormalizedStatus(selectedOrder.status))) ? 'bi-check-circle-fill' : 'bi-fire'}`}></i>
                                        <span>1. Preparación</span>
                                    </div>

                                    <i className="bi bi-arrow-right" style={{ color: '#a89c8c' }}></i>

                                    {/* Paso 2: Pasado al Cliente */}
                                    <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 10px',
                                        borderRadius: '8px',
                                        backgroundColor: getNormalizedStatus(selectedOrder.status) === 'delivered' ? '#f0e6d6' : getNormalizedStatus(selectedOrder.status) === 'completed' ? '#e6efe0' : '#f4efe6',
                                        border: getNormalizedStatus(selectedOrder.status) === 'delivered' ? '2px solid #4a3222' : getNormalizedStatus(selectedOrder.status) === 'completed' ? '1px solid #a9c9a0' : '1px solid #e6dfd3',
                                        color: getNormalizedStatus(selectedOrder.status) === 'delivered' ? '#075985' : getNormalizedStatus(selectedOrder.status) === 'completed' ? '#3d5c37' : '#8a7d70',
                                        fontWeight: getNormalizedStatus(selectedOrder.status) === 'delivered' ? '800' : '600',
                                        fontSize: '11px'
                                    }}>
                                        <i className={`bi ${getNormalizedStatus(selectedOrder.status) === 'completed' ? 'bi-check-circle-fill' : 'bi-person-check-fill'}`}></i>
                                        <span>2. Entregado</span>
                                    </div>

                                    <i className="bi bi-arrow-right" style={{ color: '#a89c8c' }}></i>

                                    {/* Paso 3: Pagado */}
                                    <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 10px',
                                        borderRadius: '8px',
                                        backgroundColor: getNormalizedStatus(selectedOrder.status) === 'completed' ? '#e6efe0' : '#f4efe6',
                                        border: getNormalizedStatus(selectedOrder.status) === 'completed' ? '2px solid #5f8a5e' : '1px solid #e6dfd3',
                                        color: getNormalizedStatus(selectedOrder.status) === 'completed' ? '#3d5c37' : '#8a7d70',
                                        fontWeight: getNormalizedStatus(selectedOrder.status) === 'completed' ? '800' : '600',
                                        fontSize: '11px'
                                    }}>
                                        <i className={`bi ${getNormalizedStatus(selectedOrder.status) === 'completed' ? 'bi-check-circle-fill' : 'bi-cash-stack'}`}></i>
                                        <span>3. Pagado</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tiempos de la orden */}
                            {(() => {
                                const t = getOrderTimes(selectedOrder, nowMs);
                                const cancelled = getNormalizedStatus(selectedOrder.status) === 'cancelled';
                                if (cancelled) return null;
                                return (
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                                        {renderTimeChip(t.prepRunning ? 'Preparando' : 'Preparación', t.prep, t.prepRunning, PREP_ALERT_MIN, 'bi-fire')}
                                        {t.wait !== null && renderTimeChip(t.waitRunning ? 'Esperando pago' : 'Hasta pagar', t.wait, t.waitRunning, WAIT_ALERT_MIN, 'bi-hourglass-split')}
                                        {selectedOrder.paid_at && renderTimeChip('Total', t.total, false, 0, 'bi-stopwatch')}
                                    </div>
                                );
                            })()}

                            {/* Cliente destacado */}
                            <div style={{
                                backgroundColor: '#f7f1e7',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid #e8d9c0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <svg
                                    style={{ width: '20px', height: '20px', color: '#4a3222' }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <div>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#3a2618', fontWeight: '500' }}>Cliente</p>
                                    <p style={{ margin: 0, fontSize: '16px', color: '#2b211b', fontWeight: '600' }}>
                                        {selectedOrder.customer_name || 'Cliente Casual'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>
                            {/* Order Info */}
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#4a3f36', marginBottom: '12px' }}>
                                    Información de la Orden
                                </h3>
                                <div style={{ backgroundColor: 'var(--sidebar-bg, #f4efe6)', padding: '16px', borderRadius: '8px' }}>
                                    <p style={{ margin: '0 0 8px 0', color: '#2b211b' }}>
                                        <strong>Tipo de Orden:</strong> {selectedOrder.order_type_display || (selectedOrder.order_type === 'takeout' ? 'Para Llevar' : 'Comer Aquí')}
                                    </p>
                                    <p style={{ margin: '0 0 8px 0', color: '#2b211b' }}>
                                        <strong>Estado:</strong>{' '}
                                        <span style={{
                                            padding: '4px 12px',
                                            borderRadius: '9999px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            backgroundColor: getNormalizedStatus(selectedOrder.status) === 'preparing' ? '#f7ecd3' :
                                                             getNormalizedStatus(selectedOrder.status) === 'delivered' ? '#f0e6d6' :
                                                             getNormalizedStatus(selectedOrder.status) === 'completed' ? '#e6efe0' : '#f7e5e0',
                                            color: getNormalizedStatus(selectedOrder.status) === 'preparing' ? '#8f6520' :
                                                   getNormalizedStatus(selectedOrder.status) === 'delivered' ? '#3a2618' :
                                                   getNormalizedStatus(selectedOrder.status) === 'completed' ? '#46693f' : '#7a3a2a'
                                        }}>
                                            {getStatusDisplay(selectedOrder.status)}
                                        </span>
                                    </p>
                                    {selectedOrder.table_number && (
                                        <p style={{ margin: '0', color: '#2b211b' }}>
                                            <strong>Mesa:</strong> {selectedOrder.table_number}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#4a3f36', marginBottom: '12px' }}>
                                    Productos
                                </h3>
                                <div style={{ border: '1px solid #e6dfd3', borderRadius: '8px', overflow: 'hidden' }}>
                                    {selectedOrder.loading ? (
                                        <div style={{ padding: '32px', textAlign: 'center' }}>
                                            <div style={{
                                                display: 'inline-block',
                                                width: '32px',
                                                height: '32px',
                                                border: '3px solid #e6dfd3',
                                                borderTopColor: '#4a3222',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            <p style={{ marginTop: '12px', color: '#8a7d70' }}>Cargando productos...</p>
                                        </div>
                                    ) : (
                                        selectedOrder.items && selectedOrder.items.length > 0 ? (
                                            selectedOrder.items.map((item, index) => {
                                                const hasDiscount = parseFloat(item.discount_percentage) > 0;
                                                const originalTotal = parseFloat(item.unit_price) * item.quantity;
                                                const itemKey = item.id ? String(item.id) : `idx_${index}`;
                                                const itemPayments = getItemPaymentMethods(selectedOrder)[itemKey] || [];
                                                const unitPaid = item.quantity > 0 ? (parseFloat(item.line_total || item.subtotal) || 0) / item.quantity : 0;
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '12px 16px',
                                                            borderBottom: index < selectedOrder.items.length - 1 ? '1px solid #e6dfd3' : 'none',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <div>
                                                            <p style={{ margin: 0, fontWeight: '500', color: '#2b211b' }}>
                                                                {item.product_details?.name || item.product_name || 'Producto'}
                                                            </p>
                                                            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#8a7d70' }}>
                                                                Cantidad: {item.quantity} × ${(parseFloat(item.unit_price) || 0).toFixed(2)}
                                                                {hasDiscount && (
                                                                    <span style={{ marginLeft: '8px', color: '#5f8a5e', fontWeight: '500' }}>
                                                                        (Desc: {parseFloat(item.discount_percentage)}%)
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {item.notes && (
                                                                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#b07f2b', fontStyle: 'italic', backgroundColor: '#fcf7ea', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                                                    Nota: {item.notes}
                                                                </p>
                                                            )}
                                                            {itemPayments.length > 0 && (
                                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                                                                    {itemPayments.map((ip, i) => (
                                                                        <span key={i} style={{ padding: '2px 7px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', backgroundColor: isCopMethod(ip.method) ? '#f7ecd3' : '#e6efe0', color: isCopMethod(ip.method) ? '#a2552a' : '#46693f', border: `1px solid ${isCopMethod(ip.method) ? '#efd6b0' : '#a9c9a0'}` }}>
                                                                            <i className="bi bi-check-circle-fill"></i> {ip.qty} pagado(s) en {PAY_METHOD_LABELS[ip.method] || ip.method}: {formatAmountInMethod(unitPaid * ip.qty, ip.method)}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            {hasDiscount && (
                                                                <p style={{ margin: '0 0 2px 0', fontSize: '12px', textDecoration: 'line-through', color: '#a89c8c' }}>
                                                                    ${originalTotal.toFixed(2)}
                                                                </p>
                                                            )}
                                                            <p style={{ margin: 0, fontWeight: '700', color: hasDiscount ? '#5f8a5e' : 'var(--primary-color, #4a3222)' }}>
                                                                ${(parseFloat(item.line_total || item.subtotal) || 0).toFixed(2)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p style={{ padding: '16px', margin: 0, color: '#8a7d70', textAlign: 'center' }}>
                                                No hay productos en esta orden
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* Order Summary */}
                            <div style={{ backgroundColor: 'var(--sidebar-bg, #f4efe6)', padding: '16px', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#8a7d70' }}>Subtotal:</span>
                                    <span style={{ fontWeight: '500', color: '#2b211b' }}>{formatOrderValue(selectedOrder.subtotal, selectedOrder.payment_method)}</span>
                                </div>
                                {selectedOrder.tax_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#8a7d70' }}>Impuestos:</span>
                                        <span style={{ fontWeight: '500', color: '#2b211b' }}>{formatOrderValue(selectedOrder.tax_amount, selectedOrder.payment_method)}</span>
                                    </div>
                                )}
                                {selectedOrder.discount_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#8a7d70' }}>Descuento:</span>
                                        <span style={{ fontWeight: '500', color: '#c0574a' }}>-{formatOrderValue(selectedOrder.discount_amount, selectedOrder.payment_method)}</span>
                                    </div>
                                )}
                                <div style={{ borderTop: '1px solid #e6dfd3', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '18px', fontWeight: '600', color: '#2b211b' }}>Total:</span>
                                    <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--primary-color, #4a3222)' }}>{formatOrderValue(selectedOrder.total, selectedOrder.payment_method)}</span>
                                </div>
                            </div>

                            {/* Información de Pago */}
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#4a3f36', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    Información de Pago
                                    {!isEditingPayment && (
                                        <button 
                                            onClick={() => {
                                                const records = selectedOrder.payment_records || [];
                                                const firstRecord = records[0];
                                                setEditPaymentRecordId(records.length > 1 ? firstRecord.id : '');
                                                setNewPaymentMethod(firstRecord ? firstRecord.payment_method : (selectedOrder.payment_method || 'efectivo'));
                                                setIsEditingPayment(true);
                                            }} 
                                            style={{ background: 'none', border: 'none', color: '#4a3222', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <i className="bi bi-pencil-square"></i> Modificar
                                        </button>
                                    )}
                                </h3>
                                <div style={{ backgroundColor: '#f4efe6', padding: '16px', borderRadius: '8px' }}>
                                    {isEditingPayment ? (
                                        <div>
                                            {(selectedOrder.payment_records || []).length > 1 && (
                                                <select
                                                    value={editPaymentRecordId}
                                                    onChange={(e) => {
                                                        setEditPaymentRecordId(e.target.value);
                                                        const record = selectedOrder.payment_records.find(r => String(r.id) === e.target.value);
                                                        if (record) setNewPaymentMethod(record.payment_method);
                                                    }}
                                                    style={{ width: '100%', padding: '8px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #d6ccbc' }}
                                                >
                                                    {selectedOrder.payment_records.map(r => (
                                                        <option key={r.id} value={r.id}>
                                                            Corregir cobro de ${parseFloat(r.amount).toFixed(2)} ({PAY_METHOD_LABELS[r.payment_method] || r.payment_method})
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                            <select
                                                value={newPaymentMethod}
                                                onChange={(e) => setNewPaymentMethod(e.target.value)}
                                                style={{ width: '100%', padding: '8px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #d6ccbc' }}
                                            >
                                                <option value="efectivo">Efectivo (USD)</option>
                                                <option value="transferencia">Transferencia (USD)</option>
                                                <option value="efectivo_cop">Efectivo (COP)</option>
                                                <option value="transferencia_cop">Transferencia (COP)</option>
                                                <option value="tarjeta">Tarjeta</option>
                                                {(selectedOrder.payment_records || []).length === 0 && (
                                                    <option value="dividido">Mixto / Dividido</option>
                                                )}
                                            </select>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button onClick={() => setIsEditingPayment(false)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d6ccbc', background: 'white', cursor: 'pointer' }}>Cancelar</button>
                                                <button onClick={handleChangePaymentMethod} style={{ padding: '6px 12px', borderRadius: '4px', border: 'none', background: '#4a3222', color: 'white', cursor: 'pointer', fontWeight: '600' }}>Guardar</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p style={{ margin: '0 0 8px 0', color: '#2b211b' }}>
                                                <strong>Método de Pago:</strong> <span>{PAY_METHOD_LABELS[selectedOrder.payment_method] || (['mixto', 'dividido'].includes(selectedOrder.payment_method) ? 'Mixto (varios métodos)' : (selectedOrder.payment_method || 'Pendiente'))}</span>
                                            </p>
                                            <p style={{ margin: '0 0 8px 0', color: '#2b211b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <strong>Estado del Pago:</strong>{' '}
                                                <span style={{
                                                    fontWeight: '700',
                                                    color: selectedOrder.payment_status === 'paid' || getNormalizedStatus(selectedOrder.status) === 'completed' ? '#46693f' : selectedOrder.payment_status === 'partially_paid' ? '#8f6520' : '#8f6520',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    {selectedOrder.payment_status === 'paid' || getNormalizedStatus(selectedOrder.status) === 'completed' ? (
                                                        <><i className="bi bi-check-circle-fill"></i> Pagado</>
                                                    ) : selectedOrder.payment_status === 'partially_paid' ? (
                                                        <><i className="bi bi-pie-chart-fill"></i> Pago Parcial</>
                                                    ) : (
                                                        <><i className="bi bi-clock-history"></i> Pendiente de Cobro</>
                                                    )}
                                                </span>
                                            </p>
                                            {selectedOrder.payment_reference && (
                                                <p style={{ margin: '0', color: '#2b211b' }}>
                                                    <strong>Referencia:</strong> {selectedOrder.payment_reference}
                                                </p>
                                            )}
                                            {getOrderPayments(selectedOrder).length > 0 && (
                                                <div style={{ marginTop: '10px', borderTop: '1px solid #e6dfd3', paddingTop: '10px' }}>
                                                    <strong style={{ fontSize: '13px', color: '#2b211b' }}>Pagos registrados:</strong>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                                                        {getOrderPayments(selectedOrder).map((pay, idx) => (
                                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#4a3f36', backgroundColor: '#ffffff', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e6dfd3' }}>
                                                                <span>
                                                                    {pay.payer ? <strong>{pay.payer} • </strong> : null}
                                                                    {PAY_METHOD_LABELS[pay.method] || pay.method}
                                                                    {pay.created_at && (
                                                                        <span style={{ color: '#a89c8c', marginLeft: '6px' }}>
                                                                            {new Date(pay.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <strong>
                                                                    {formatAmountInMethod(pay.amount, pay.method)}
                                                                    {isCopMethod(pay.method) && <span style={{ color: '#a89c8c', fontWeight: '500', marginLeft: '6px' }}>(${pay.amount.toFixed(2)})</span>}
                                                                </strong>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* SECCIÓN FACTURACIÓN ELECTRÓNICA SRI (KoaDoc) */}
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#4a3f36', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span><i className="bi bi-file-earmark-check" style={{ color: '#4a3222' }}></i> Facturación Electrónica SRI</span>
                                    {selectedOrder.sri_status === 'AUTHORIZED' ? (
                                        <span style={{ fontSize: '12px', color: '#46693f', backgroundColor: '#e6efe0', padding: '3px 8px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <i className="bi bi-check-lg"></i> Autorizado SRI
                                        </span>
                                    ) : selectedOrder.sri_status === 'QUEUED' ? (
                                        <span style={{ fontSize: '12px', color: '#3a2618', backgroundColor: '#f0e6d6', padding: '3px 8px', borderRadius: '10px' }}>
                                            En Cola SRI
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: '12px', color: '#8a7d70', backgroundColor: '#f4efe6', padding: '3px 8px', borderRadius: '10px' }}>
                                            {selectedOrder.sri_status || 'No Emitida'}
                                        </span>
                                    )}
                                </h3>
                                <div style={{ backgroundColor: '#faf7f1', border: '1px solid #e6dfd3', padding: '16px', borderRadius: '8px' }}>
                                    {selectedOrder.sri_document_number && (
                                        <p style={{ margin: '0 0 6px 0', fontSize: '14px', color: '#2b211b' }}>
                                            <strong>N° Comprobante:</strong> {selectedOrder.sri_document_number}
                                        </p>
                                    )}
                                    {selectedOrder.sri_access_key && (
                                        <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#6b5e51', wordBreak: 'break-all' }}>
                                            <strong>Clave de Acceso:</strong> <code>{selectedOrder.sri_access_key}</code>
                                        </p>
                                    )}
                                    {selectedOrder.sri_error_message && (
                                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: selectedOrder.sri_status === 'AUTHORIZED' ? '#46693f' : '#96372c' }}>
                                            <strong>Mensaje:</strong> {selectedOrder.sri_error_message}
                                        </p>
                                    )}

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                                        {selectedOrder.sri_pdf_url && (
                                            <a
                                                href={selectedOrder.sri_pdf_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#e6efe0',
                                                    color: '#46693f',
                                                    border: '1px solid #a9c9a0',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-file-earmark-pdf-fill"></i> Descargar RIDE (PDF)
                                            </a>
                                        )}
                                        {selectedOrder.sri_xml_url && (
                                            <a
                                                href={selectedOrder.sri_xml_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f4efe6',
                                                    color: '#6b5e51',
                                                    border: '1px solid #d6ccbc',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-code-square"></i> Descargar XML
                                            </a>
                                        )}
                                        {selectedOrder.sri_id && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const res = await billingService.getOrderInvoiceStatus(selectedOrder.id);
                                                        if (res.success) {
                                                            alert(`Estado actual SRI: ${res.data?.status || 'Actualizado'}`);
                                                            fetchOrders();
                                                            closeModal();
                                                        } else {
                                                            alert(res.message || 'No se pudo actualizar el estado.');
                                                        }
                                                    } catch (err) {
                                                        alert('Error al consultar estado: ' + (err.response?.data?.message || err.message));
                                                    }
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f4efe6',
                                                    color: '#4a3f36',
                                                    border: '1px solid #d6ccbc',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-arrow-clockwise"></i> Consultar Estado SRI
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const res = await billingService.emitOrderInvoice(selectedOrder.id);
                                                    alert(res.message || 'Factura emitida con éxito.');
                                                    fetchOrders();
                                                    closeModal();
                                                } catch (err) {
                                                    alert('Error al emitir factura: ' + (err.response?.data?.message || err.message));
                                                }
                                            }}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                backgroundColor: '#4a3222',
                                                color: 'white',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <i className="bi bi-send-fill"></i> {selectedOrder.sri_status === 'AUTHORIZED' ? 'Reenviar Factura' : 'Emitir al SRI'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* SECCIÓN DE NOTAS */}
                            {selectedOrder.notes && (
                                <div style={{ marginTop: '24px' }}>
                                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#4a3f36', marginBottom: '12px' }}>
                                        Notas
                                    </h3>
                                    <p style={{ backgroundColor: '#f7ecd3', padding: '12px', borderRadius: '8px', margin: 0, color: '#6f4d17' }}>
                                        {formatOrderNotes(selectedOrder.notes)}
                                    </p>
                                </div>
                            )}

                            {/* BOTONES DE ACCIÓN (FOOTER DEL MODAL) */}
                            <div style={{ marginTop: '32px', borderTop: '1px solid #e6dfd3', paddingTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                {isAdmin && selectedOrder.status !== 'cancelled' && selectedOrder.status_display?.toLowerCase() !== 'anulada' && (
                                    <button
                                        onClick={handleCancelOrder}
                                        style={{
                                            padding: '10px 18px',
                                            backgroundColor: '#f7e5e0',
                                            color: '#96372c',
                                            border: '1px solid #e6ad9f',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            marginRight: 'auto',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Anular Orden
                                    </button>
                                )}

                                {/* Botón Avanzar a Pasado al Cliente */}
                                {getNormalizedStatus(selectedOrder.status) === 'preparing' && (
                                    <button
                                        onClick={async () => {
                                            await handleStatusChange(selectedOrder.order_number, 'delivered');
                                        }}
                                        style={{
                                            padding: '10px 18px',
                                            backgroundColor: '#4a3222',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <i className="bi bi-person-check-fill"></i> Pasar al Cliente
                                    </button>
                                )}

                                {/* Botón Editar orden */}
                                {canEditOrder(selectedOrder) && (
                                    <button
                                        onClick={() => handleEditOrder(selectedOrder)}
                                        style={{
                                            padding: '10px 18px',
                                            backgroundColor: '#ffffff',
                                            color: '#4a3222',
                                            border: '1px solid #4a3222',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <i className="bi bi-pencil-square"></i> Editar orden
                                    </button>
                                )}

                                {/* Botón Cobrar */}
                                {getNormalizedStatus(selectedOrder.status) !== 'completed' && getNormalizedStatus(selectedOrder.status) !== 'cancelled' && (
                                    <button
                                        onClick={() => {
                                            handleOpenPayModal(selectedOrder);
                                        }}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#3d5c37',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            boxShadow: '0 2px 4px rgba(22, 101, 52, 0.2)'
                                        }}
                                    >
                                        <i className="bi bi-cash-stack"></i> Cobrar Orden
                                    </button>
                                )}

                                <button
                                    onClick={closeModal}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#f4efe6',
                                        color: '#4a3f36',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                    }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para configurar número de orden secuencial y reinicio diario */}
            <ConfiguracionSecuenciaModal
                isOpen={showSequenceModal}
                onClose={() => setShowSequenceModal(false)}
            />

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulseDot {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.25; }
                }
                @keyframes modalSlide {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default Ordenes;